/* eslint-disable @typescript-eslint/no-explicit-any -- Mock data returns dynamic shapes matching API responses */
/**
 * Comprehensive mock data for demo mode - "Lens Failure Scenario Walkthrough"
 *
 * Narrative: An AI system in production starts behaving incorrectly.
 * Root cause: stale dataset "customer-interactions-v3" (36h stale, SLA is 6h).
 * This feeds a GenAI prompt ("product-recommendation") and an agent ("customer-support-agent").
 * The stale data causes: cost spike, evaluation regression (0.92 -> 0.71),
 * an unsafe agent action, and a safety violation.
 *
 * All IDs are stable/deterministic so mock data is consistent across reloads.
 */

// ============================================================================
// STABLE DETERMINISTIC IDs
// ============================================================================

// Client/tenant
export const DEMO_CLIENT_ID = 'c0000000-0000-4000-a000-000000000001';
const DEMO_TENANT_ID = 't0000000-0000-4000-a000-000000000001';

// Projects
export const PROJECT_AI_TRANSFORM_ID = 'p0000000-0000-4000-a000-000000000001';
export const PROJECT_CLOUD_MIGRATION_ID = 'p0000000-0000-4000-a000-000000000002';
export const PROJECT_DATA_LAKE_ID = 'p0000000-0000-4000-a000-000000000003';
export const PROJECT_SECURITY_ID = 'p0000000-0000-4000-a000-000000000004';

// People
const PERSON_SARAH_ID = 'u0000000-0000-4000-a000-000000000001';
const PERSON_MARCUS_ID = 'u0000000-0000-4000-a000-000000000002';
const PERSON_EMILY_ID = 'u0000000-0000-4000-a000-000000000003';
const PERSON_JAMES_ID = 'u0000000-0000-4000-a000-000000000004';

// AWS Environment
const AWS_ENV_PROD_ID = 'e0000000-0000-4000-a000-000000000001';
const AWS_ENV_DEV_ID = 'e0000000-0000-4000-a000-000000000002';

// DataOps - the critical dataset
const DATASET_CUSTOMER_INTERACTIONS_ID = 'd0000000-0000-4000-a000-000000000001';
const DATASET_PRODUCT_CATALOG_ID = 'd0000000-0000-4000-a000-000000000002';
const DATASET_USER_EVENTS_ID = 'd0000000-0000-4000-a000-000000000003';

// GenAIOps - the prompt that regresses
const PROMPT_CUSTOMER_CLASSIFIER_ID = 'pr000000-0000-4000-a000-000000000001';
const PROMPT_CUSTOMER_CLASSIFIER_V1_ID = 'pr000000-0000-4000-a000-000000000010';
const PROMPT_CODE_REVIEW_ID = 'pr000000-0000-4000-a000-000000000002';
const PROMPT_MARKETING_ID = 'pr000000-0000-4000-a000-000000000003';

// Evaluation runs
const EVAL_BASELINE_ID = 'ev000000-0000-4000-a000-000000000001';
const EVAL_REGRESSION_ID = 'ev000000-0000-4000-a000-000000000002';
const EVAL_MONTHLY_ID = 'ev000000-0000-4000-a000-000000000003';
const EVAL_WEEKLY_ID = 'ev000000-0000-4000-a000-000000000004';

// Traces
const TRACE_GOOD_ID = 'tr000000-0000-4000-a000-000000000001';
const TRACE_BAD_ID = 'tr000000-0000-4000-a000-000000000002';
const TRACE_ERROR_ID = 'tr000000-0000-4000-a000-000000000003';
const TRACE_BASELINE_1_ID = 'tr000000-0000-4000-a000-000000000004';
const TRACE_BASELINE_2_ID = 'tr000000-0000-4000-a000-000000000005';
const TRACE_BASELINE_3_ID = 'tr000000-0000-4000-a000-000000000006';
const TRACE_DEGRADED_1_ID = 'tr000000-0000-4000-a000-000000000007';
const TRACE_DEGRADED_2_ID = 'tr000000-0000-4000-a000-000000000008';

// AgentOps
const AGENT_ESCALATION_ID = 'ag000000-0000-4000-a000-000000000001';
const AGENT_RUN_GOOD_ID = 'ar000000-0000-4000-a000-000000000001';
const AGENT_RUN_BAD_ID = 'ar000000-0000-4000-a000-000000000002';
const AGENT_RUN_NORMAL_ID = 'ar000000-0000-4000-a000-000000000003';
const AGENT_RUN_HIST_1_ID = 'ar000000-0000-4000-a000-000000000004';
const AGENT_RUN_HIST_2_ID = 'ar000000-0000-4000-a000-000000000005';
const AGENT_RUN_HIST_3_ID = 'ar000000-0000-4000-a000-000000000006';
const AGENT_RUN_HIST_4_ID = 'ar000000-0000-4000-a000-000000000007';

// Safety
const SAFETY_VIOLATION_ID = 'sv000000-0000-4000-a000-000000000001';

// Incident
const INCIDENT_ID = 'in000000-0000-4000-a000-000000000001';

// Milestones
const MILESTONE_1_ID = 'ms000000-0000-4000-a000-000000000001';
const MILESTONE_2_ID = 'ms000000-0000-4000-a000-000000000002';
const MILESTONE_3_ID = 'ms000000-0000-4000-a000-000000000003';
const MILESTONE_4_ID = 'ms000000-0000-4000-a000-000000000004';
const MILESTONE_5_ID = 'ms000000-0000-4000-a000-000000000005';

// Support tickets
export const TICKET_1_ID = 'st000000-0000-4000-a000-000000000001';
export const TICKET_2_ID = 'st000000-0000-4000-a000-000000000002';
export const TICKET_3_ID = 'st000000-0000-4000-a000-000000000003';

// Sessions
const SESSION_1_ID = 'se000000-0000-4000-a000-000000000001';
const SESSION_2_ID = 'se000000-0000-4000-a000-000000000002';
const SESSION_3_ID = 'se000000-0000-4000-a000-000000000003';
const SESSION_4_ID = 'se000000-0000-4000-a000-000000000004';
const SESSION_5_ID = 'se000000-0000-4000-a000-000000000005';

// Anomaly
const ANOMALY_ID = 'an000000-0000-4000-a000-000000000001';

// ============================================================================
// HELPERS
// ============================================================================

const daysAgo = (days: number): string => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString();
};

const hoursAgo = (hours: number): string => {
  const date = new Date();
  date.setHours(date.getHours() - hours);
  return date.toISOString();
};

const daysFromNow = (days: number): string => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString();
};

const DEMO_APM_AGENTS = ['customer-support-agent', 'data-pipeline-agent', 'code-review-agent'] as const;

type DemoAPMAgentProfile = {
  heapPercent: number;
  heapUsed: number;
  heapTotal: number;
  heapMax: number;
  rss: number;
  cpuPercent: number;
  cpuMax: number;
  threadCount: number;
  threadActive: number;
  threadBlocked: number;
  threadWaiting: number;
  healthScore: number;
  healthStatus: 'healthy' | 'warning' | 'critical';
  leakScore: number;
  leakConfidence: 'low' | 'medium' | 'high';
  leakGrowthRate: number;
  alertSeverity: 'warning' | 'critical' | null;
};

const DEMO_APM_AGENT_PROFILES: Record<string, DemoAPMAgentProfile> = {
  'customer-support-agent': {
    heapPercent: 72.3,
    heapUsed: 386547712,
    heapTotal: 536870912,
    heapMax: 1073741824,
    rss: 524288000,
    cpuPercent: 32.5,
    cpuMax: 58.3,
    threadCount: 24,
    threadActive: 18,
    threadBlocked: 1,
    threadWaiting: 5,
    healthScore: 78,
    healthStatus: 'warning',
    leakScore: 0.12,
    leakConfidence: 'low',
    leakGrowthRate: 838860,
    alertSeverity: 'warning',
  },
  'data-pipeline-agent': {
    heapPercent: 58.4,
    heapUsed: 313524224,
    heapTotal: 536870912,
    heapMax: 1073741824,
    rss: 642777088,
    cpuPercent: 44.8,
    cpuMax: 71.2,
    threadCount: 14,
    threadActive: 10,
    threadBlocked: 0,
    threadWaiting: 4,
    healthScore: 86,
    healthStatus: 'healthy',
    leakScore: 0.08,
    leakConfidence: 'low',
    leakGrowthRate: 524288,
    alertSeverity: null,
  },
  'code-review-agent': {
    heapPercent: 81.1,
    heapUsed: 435159040,
    heapTotal: 536870912,
    heapMax: 1073741824,
    rss: 486539264,
    cpuPercent: 67.2,
    cpuMax: 89.4,
    threadCount: 31,
    threadActive: 23,
    threadBlocked: 2,
    threadWaiting: 6,
    healthScore: 68,
    healthStatus: 'warning',
    leakScore: 0.29,
    leakConfidence: 'medium',
    leakGrowthRate: 1835008,
    alertSeverity: 'critical',
  },
};

const hashString = (value: string): number => {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 31 + value.charCodeAt(i)) >>> 0;
  }
  return hash;
};

const getDemoAPMAgentProfile = (agentName: string | null): DemoAPMAgentProfile => {
  const normalized = agentName || 'customer-support-agent';
  const preset = DEMO_APM_AGENT_PROFILES[normalized];
  if (preset) return preset;

  const hash = hashString(normalized);
  const heapPercent = 55 + (hash % 25);
  const cpuPercent = 20 + ((hash >> 3) % 40);
  const threadCount = 12 + ((hash >> 5) % 16);

  return {
    heapPercent,
    heapUsed: Math.round((heapPercent / 100) * 536870912),
    heapTotal: 536870912,
    heapMax: 1073741824,
    rss: 402653184 + ((hash % 180) * 1048576),
    cpuPercent,
    cpuMax: Math.min(cpuPercent + 18, 95),
    threadCount,
    threadActive: Math.max(threadCount - 5, 4),
    threadBlocked: hash % 2,
    threadWaiting: 3 + ((hash >> 7) % 4),
    healthScore: Math.max(60, 95 - Math.round(heapPercent / 2) - Math.round(cpuPercent / 5)),
    healthStatus: heapPercent >= 80 || cpuPercent >= 75 ? 'warning' : 'healthy',
    leakScore: Number((0.05 + ((hash % 20) / 100)).toFixed(2)),
    leakConfidence: heapPercent >= 75 ? 'medium' : 'low',
    leakGrowthRate: 400000 + ((hash % 1200) * 1024),
    alertSeverity: heapPercent >= 80 ? 'warning' : null,
  };
};

const buildDemoAPMAlert = (agentName: string, profile: DemoAPMAgentProfile) => {
  if (!profile.alertSeverity) return null;

  const isCritical = profile.alertSeverity === 'critical';
  return {
    id: `apm-alert-${agentName}`,
    title: isCritical ? 'Sustained High CPU Usage' : 'High Memory Usage',
    description: isCritical
      ? `CPU usage remained elevated on ${agentName}`
      : `Heap memory usage exceeded warning threshold on ${agentName}`,
    severity: profile.alertSeverity,
    agent_name: agentName,
    triggered_at: '2025-01-15T14:10:00Z',
    actual_value: isCritical ? profile.cpuPercent : profile.heapPercent,
    threshold_value: isCritical ? 85 : 70,
    status: 'active',
  };
};

// ============================================================================
// PROJECTS
// ============================================================================

export const mockProjects = [
  {
    id: PROJECT_AI_TRANSFORM_ID,
    name: 'AI Transformation Initiative',
    description: 'Comprehensive AI transformation project including ML Ops infrastructure, data pipeline modernization, and GenAI integration',
    client_id: DEMO_CLIENT_ID,
    client_name: 'TechCorp Industries',
    status: 'active',
    start_date: daysAgo(60),
    end_date: daysFromNow(90),
    budget: 75000,
    infrastructure_budget: 50000,
    delivery_budget: 25000,
    spent: 28000,
    total_spent: 28000,
    infrastructure_spent: 21000,
    delivery_spent: 7000,
    currency: 'USD',
    progress: 33,
    has_sow: true,
    sow_google_drive_url: 'https://drive.google.com/drive/folders/1abc-demo-folder',
    cost_tracking_enabled: true,
    cost_tag_key: 'Project',
    cost_tag_value: 'ai-transformation-initiative',
    created_at: daysAgo(60),
    updated_at: daysAgo(2)
  },
  {
    id: PROJECT_CLOUD_MIGRATION_ID,
    name: 'Cloud Migration - Phase 2',
    description: 'Migration of legacy applications to AWS with containerization and serverless architecture',
    client_id: DEMO_CLIENT_ID,
    client_name: 'TechCorp Industries',
    status: 'active',
    start_date: daysAgo(30),
    end_date: daysFromNow(120),
    budget: 50000,
    infrastructure_budget: 32000,
    delivery_budget: 18000,
    spent: 12500,
    total_spent: 12500,
    infrastructure_spent: 8500,
    delivery_spent: 4000,
    currency: 'USD',
    progress: 50,
    has_sow: true,
    sow_google_drive_url: 'https://drive.google.com/drive/folders/2def-demo-folder',
    cost_tracking_enabled: true,
    cost_tag_key: 'Project',
    cost_tag_value: 'cloud-migration-phase-2',
    created_at: daysAgo(30),
    updated_at: daysAgo(1)
  },
  {
    id: PROJECT_DATA_LAKE_ID,
    name: 'Data Lake Implementation',
    description: 'Build enterprise data lake with AWS Lake Formation, Glue, and Athena',
    client_id: DEMO_CLIENT_ID,
    client_name: 'TechCorp Industries',
    status: 'planning',
    start_date: daysFromNow(14),
    end_date: daysFromNow(180),
    budget: 60000,
    infrastructure_budget: 42000,
    delivery_budget: 18000,
    spent: 0,
    total_spent: 0,
    infrastructure_spent: 0,
    delivery_spent: 0,
    currency: 'USD',
    progress: 0,
    has_sow: false,
    sow_google_drive_url: null,
    cost_tracking_enabled: false,
    cost_tag_key: null,
    cost_tag_value: null,
    created_at: daysAgo(15),
    updated_at: daysAgo(3)
  },
  {
    id: PROJECT_SECURITY_ID,
    name: 'Security & Compliance Upgrade',
    description: 'Implement AWS security best practices, GuardDuty, Security Hub, and compliance automation',
    client_id: DEMO_CLIENT_ID,
    client_name: 'TechCorp Industries',
    status: 'completed',
    start_date: daysAgo(180),
    end_date: daysAgo(30),
    budget: 30000,
    infrastructure_budget: 18000,
    delivery_budget: 12000,
    spent: 28500,
    total_spent: 28500,
    infrastructure_spent: 17200,
    delivery_spent: 11300,
    currency: 'USD',
    progress: 100,
    has_sow: true,
    sow_google_drive_url: null,
    sow_file_name: 'Security_Compliance_SoW_v1.pdf',
    cost_tracking_enabled: true,
    cost_tag_key: 'Project',
    cost_tag_value: 'security-compliance-upgrade',
    created_at: daysAgo(180),
    updated_at: daysAgo(30)
  }
];

// ============================================================================
// CLIENTS
// ============================================================================

export const mockClients = [
  {
    id: DEMO_CLIENT_ID,
    name: 'TechCorp Industries',
    status: 'active',
    subscription_plan: 'paid',
    contact_email: 'admin@techcorp.com',
    created_at: daysAgo(365)
  }
];

// ============================================================================
// TIMESHEETS
// ============================================================================

export const mockTimesheets = [
  {
    id: 'ts000000-0000-4000-a000-000000000001',
    project_id: PROJECT_AI_TRANSFORM_ID,
    project_name: mockProjects[0].name,
    user_id: PERSON_SARAH_ID,
    user_name: 'Sarah Chen',
    employee_id: PERSON_SARAH_ID,
    employee_name: 'Sarah Chen',
    date: daysAgo(1),
    hours: 8,
    description: 'ML pipeline development and testing',
    billable: true,
    created_at: daysAgo(1)
  },
  {
    id: 'ts000000-0000-4000-a000-000000000002',
    project_id: PROJECT_AI_TRANSFORM_ID,
    project_name: mockProjects[0].name,
    user_id: PERSON_MARCUS_ID,
    user_name: 'Marcus Rodriguez',
    employee_id: PERSON_MARCUS_ID,
    employee_name: 'Marcus Rodriguez',
    date: daysAgo(1),
    hours: 6,
    description: 'GenAI model integration',
    billable: true,
    created_at: daysAgo(1)
  },
  {
    id: 'ts000000-0000-4000-a000-000000000003',
    project_id: PROJECT_CLOUD_MIGRATION_ID,
    project_name: mockProjects[1].name,
    user_id: PERSON_EMILY_ID,
    user_name: 'Emily Watson',
    employee_id: PERSON_EMILY_ID,
    employee_name: 'Emily Watson',
    date: daysAgo(2),
    hours: 7.5,
    description: 'Container orchestration setup',
    billable: true,
    created_at: daysAgo(2)
  },
  {
    id: 'ts000000-0000-4000-a000-000000000004',
    project_id: PROJECT_CLOUD_MIGRATION_ID,
    project_name: mockProjects[1].name,
    user_id: PERSON_JAMES_ID,
    user_name: 'James Kim',
    employee_id: PERSON_JAMES_ID,
    employee_name: 'James Kim',
    date: daysAgo(3),
    hours: 8,
    description: 'Application migration and testing',
    billable: true,
    created_at: daysAgo(3)
  }
];

// ============================================================================
// MILESTONES
// ============================================================================

export const mockMilestones = [
  {
    id: MILESTONE_1_ID,
    project_id: PROJECT_AI_TRANSFORM_ID,
    name: 'Data Pipeline Setup',
    description: 'Complete data ingestion and processing pipeline',
    due_date: daysAgo(10),
    status: 'COMPLETED',
    progress: 100,
    created_at: daysAgo(60)
  },
  {
    id: MILESTONE_2_ID,
    project_id: PROJECT_AI_TRANSFORM_ID,
    name: 'ML Model Training',
    description: 'Train and validate initial ML models',
    due_date: daysFromNow(15),
    status: 'IN_PROGRESS',
    progress: 65,
    created_at: daysAgo(30)
  },
  {
    id: MILESTONE_3_ID,
    project_id: PROJECT_AI_TRANSFORM_ID,
    name: 'GenAI Integration',
    description: 'Integrate GenAI capabilities into platform',
    due_date: daysFromNow(45),
    status: 'PLANNED',
    progress: 0,
    created_at: daysAgo(15)
  },
  {
    id: MILESTONE_4_ID,
    project_id: PROJECT_CLOUD_MIGRATION_ID,
    name: 'Application Assessment',
    description: 'Assess all applications for cloud readiness',
    due_date: daysAgo(5),
    status: 'COMPLETED',
    progress: 100,
    created_at: daysAgo(30)
  },
  {
    id: MILESTONE_5_ID,
    project_id: PROJECT_CLOUD_MIGRATION_ID,
    name: 'Containerization',
    description: 'Containerize legacy applications',
    due_date: daysFromNow(30),
    status: 'IN_PROGRESS',
    progress: 45,
    created_at: daysAgo(20)
  }
];

// ============================================================================
// SUPPORT TICKETS
// ============================================================================

export const mockSupportTickets = [
  {
    id: TICKET_1_ID,
    subject: 'API Gateway timeout issues',
    title: 'API Gateway timeout issues',
    description: 'Experiencing intermittent timeouts on API Gateway endpoints during peak hours',
    project_id: PROJECT_AI_TRANSFORM_ID,
    project_name: mockProjects[0].name,
    status: 'open',
    priority: 'high',
    created_by: 'John Doe',
    created_at: daysAgo(2),
    updated_at: daysAgo(1),
    comments: [
      { id: 'cm000001', author: 'Support Team', content: 'We are investigating the timeout issues. Can you provide CloudWatch logs?', created_at: daysAgo(1) }
    ]
  },
  {
    id: TICKET_2_ID,
    subject: 'Request for additional S3 bucket',
    title: 'Request for additional S3 bucket',
    description: 'Need new S3 bucket for storing ML training data with lifecycle policies',
    project_id: PROJECT_AI_TRANSFORM_ID,
    project_name: mockProjects[0].name,
    status: 'in_progress',
    priority: 'medium',
    created_by: 'Sarah Chen',
    created_at: daysAgo(5),
    updated_at: daysAgo(3),
    comments: [
      { id: 'cm000002', author: 'DevOps Team', content: 'Bucket created. Configuring lifecycle policies now.', created_at: daysAgo(3) }
    ]
  },
  {
    id: TICKET_3_ID,
    subject: 'IAM permissions for new team member',
    title: 'IAM permissions for new team member',
    description: 'New developer needs access to development environment',
    project_id: PROJECT_CLOUD_MIGRATION_ID,
    project_name: mockProjects[1].name,
    status: 'resolved',
    priority: 'low',
    created_by: 'Emily Watson',
    created_at: daysAgo(10),
    updated_at: daysAgo(8),
    comments: [
      { id: 'cm000003', author: 'Security Team', content: 'Permissions granted. User has been added to dev group.', created_at: daysAgo(8) }
    ]
  }
];

// ============================================================================
// FINOPS - COSTS (elevated AI-heavy spend - Act 2)
// ============================================================================

export const mockCosts = {
  summary: {
    current_month: 3480.18,
    last_month: 2900.42,
    change_percentage: 20,
    year_to_date: 28550,
    budget_remaining: 6519.82,
    projected_month_end: 3850
  },
  by_service: [
    { service: 'Amazon SageMaker', cost: 950.5, percentage: 27.3 },
    { service: 'Amazon Bedrock', cost: 790.4, percentage: 22.7 },
    { service: 'EC2', cost: 680.5, percentage: 19.5 },
    { service: 'RDS', cost: 460.1, percentage: 13.2 },
    { service: 'S3', cost: 300.2, percentage: 8.6 },
    { service: 'Other', cost: 298.48, percentage: 8.6 }
  ],
  by_project: mockProjects.map((project, index) => ({
    project_id: project.id,
    project_name: project.name,
    cost: [1605.5, 828.3, 597.2, 449.18][index] || 0,
    percentage: [46.1, 23.8, 17.1, 13][index] || 0
  })),
  daily_trend: Array.from({ length: 30 }, (_, i) => ({
    date: daysAgo(29 - i),
    cost: i < 20
      ? 80 + Math.sin(i * 0.3) * 12
      : 80 + (i - 20) * 8 + Math.sin(i * 0.3) * 12
  }))
};

// ============================================================================
// FINOPS - BUDGETS
// ============================================================================

export const mockBudgets = [
  {
    id: 'bg000000-0000-4000-a000-000000000001',
    name: 'Monthly AWS Spend',
    amount: 3000,
    spent: 3480.18,
    period: 'monthly',
    alert_threshold: 80,
    status: 'exceeded',
    created_at: daysAgo(180)
  },
  {
    id: 'bg000000-0000-4000-a000-000000000002',
    name: 'Q1 2026 Budget',
    amount: 10000,
    spent: 6680.6,
    period: 'quarterly',
    alert_threshold: 90,
    status: 'warning',
    created_at: daysAgo(90)
  }
];

// ============================================================================
// TAGGING COMPLIANCE
// ============================================================================

export const mockTaggingCompliance = {
  compliance: {
    complianceRate: 78.8,
    trend: 2.3,
    taggedResources: 78,
    totalResources: 99,
    untaggedResources: 21
  },
  costAllocation: {
    allocationRate: 71.2,
    totalCost: 3480,
    allocatedCost: 2478,
    unallocatedCost: 1002
  },
  tagCoverage: [
    { tagKey: 'Environment', coverage: 92.3, resourceCount: 91 },
    { tagKey: 'Project', coverage: 78.5, resourceCount: 78 },
    { tagKey: 'Owner', coverage: 85.1, resourceCount: 84 },
    { tagKey: 'CostCenter', coverage: 71.2, resourceCount: 70 },
    { tagKey: 'Application', coverage: 68.9, resourceCount: 68 },
    { tagKey: 'DataClassification', coverage: 62.4, resourceCount: 62 }
  ],
  policies: [
    { id: 'tp000001', name: 'Required Tags', description: 'Enforce Environment, Project, and Owner tags on all resources', status: 'active', complianceRate: 78.8, compliantResources: 78, nonCompliantResources: 21, requiredTags: ['Environment', 'Project', 'Owner'], enforcementLevel: 'Mandatory', created_at: daysAgo(120) },
    { id: 'tp000002', name: 'Environment Values', description: 'Environment tag must be one of: dev, staging, prod', status: 'active', complianceRate: 95.2, compliantResources: 94, nonCompliantResources: 5, requiredTags: ['Environment'], validValues: ['dev', 'staging', 'prod'], enforcementLevel: 'Mandatory', created_at: daysAgo(120) },
    { id: 'tp000003', name: 'Cost Allocation', description: 'CostCenter tag required for billing resources', status: 'active', complianceRate: 71.2, compliantResources: 70, nonCompliantResources: 29, requiredTags: ['CostCenter'], enforcementLevel: 'Advisory', created_at: daysAgo(90) }
  ],
  resourcesByTag: [
    { tag: 'Environment:production', count: 24, cost: 1680, services: ['EC2', 'RDS', 'S3', 'Lambda'] },
    { tag: 'Environment:staging', count: 12, cost: 460, services: ['EC2', 'RDS', 'S3'] },
    { tag: 'Environment:dev', count: 18, cost: 300, services: ['EC2', 'S3', 'Lambda'] },
    { tag: 'Project:lens-platform', count: 18, cost: 575, services: ['EC2', 'RDS', 'Lambda'] },
    { tag: 'Project:ml-pipeline', count: 8, cost: 950, services: ['EC2', 'SageMaker', 'S3'] },
    { tag: 'CostCenter:CC-1001', count: 16, cost: 510, services: ['EC2', 'RDS', 'S3'] }
  ],
  untaggedResources: [
    { resourceId: 'i-0a1b2c3d4e5f6g7h8', resourceType: 'EC2 Instance', region: 'us-east-1', cost: 234.56, age: 45 },
    { resourceId: 'db-instance-prod-replica', resourceType: 'RDS Instance', region: 'us-west-2', cost: 456.78, age: 60 },
    { resourceId: 'my-data-bucket-2026', resourceType: 'S3 Bucket', region: 'us-east-1', cost: 123.45, age: 120 },
    { resourceId: 'data-processor-v2', resourceType: 'Lambda Function', region: 'eu-west-1', cost: 45.67, age: 30 }
  ]
};

// ============================================================================
// GUARDRAILS
// ============================================================================

export const mockGuardrails = {
  summary: {
    compliance_score: 87.5,
    total_rules: 48,
    passing: 42,
    failing: 6,
    last_check: daysAgo(0)
  },
  rules: [
    { id: 'gr000001', name: 'S3 Bucket Encryption', description: 'All S3 buckets must have encryption enabled', category: 'security', status: 'passing', severity: 'high', resources_checked: 12, resources_compliant: 12, group_ids: ['gg000001'], resource_types: ['s3'] },
    { id: 'gr000002', name: 'RDS Multi-AZ', description: 'Production RDS instances must have Multi-AZ enabled', category: 'reliability', status: 'failing', severity: 'high', resources_checked: 2, resources_compliant: 1, group_ids: ['gg000003'], resource_types: ['rds'] },
    { id: 'gr000003', name: 'EC2 Instance Types', description: 'Only approved instance types can be used', category: 'cost', status: 'passing', severity: 'medium', resources_checked: 7, resources_compliant: 7, group_ids: ['gg000002'], resource_types: ['ec2'] },
    { id: 'gr000004', name: 'IAM Password Policy', description: 'Password policy must meet security requirements', category: 'security', status: 'passing', severity: 'critical', resources_checked: 1, resources_compliant: 1, group_ids: ['gg000001'], resource_types: ['iam'] },
    { id: 'gr000005', name: 'VPC Flow Logs', description: 'All VPCs must have flow logs enabled', category: 'security', status: 'failing', severity: 'medium', resources_checked: 8, resources_compliant: 6, group_ids: ['gg000001'], resource_types: ['ec2'] }
  ]
};

// ============================================================================
// DATAOPS - DATASETS (Act 3 - the root cause)
// customer-interactions-v3 is 36h stale (SLA is 6h)
// ============================================================================

export const mockDatasets = [
  {
    id: DATASET_CUSTOMER_INTERACTIONS_ID,
    name: 'customer-interactions-v3',
    database: 'production',
    schema: 'ai_features',
    table_type: 'table',
    owner: 'data-platform@techcorp.com',
    description: 'Customer interaction records feeding AI classification and escalation models. SLA: refreshed every 6 hours.',
    row_count: 8234567,
    size_mb: 4532,
    last_updated: hoursAgo(36),
    quality_score: 64.2,
    freshness_status: 'stale',
    freshness_sla_hours: 6,
    freshness_actual_hours: 36,
    tags: ['ai-critical', 'customer-data', 'pii'],
    columns: 28,
    downstream_consumers: [
      { type: 'prompt', id: PROMPT_CUSTOMER_CLASSIFIER_ID, name: 'product-recommendation' },
      { type: 'agent', id: AGENT_ESCALATION_ID, name: 'customer-support-agent' }
    ]
  },
  {
    id: DATASET_PRODUCT_CATALOG_ID,
    name: 'product-catalog',
    database: 'production',
    schema: 'core',
    table_type: 'table',
    owner: 'product-team@techcorp.com',
    description: 'Master product catalog with pricing and inventory',
    row_count: 87654,
    size_mb: 234,
    last_updated: daysAgo(1),
    quality_score: 98.7,
    freshness_status: 'fresh',
    tags: ['master-data', 'product'],
    columns: 15
  },
  {
    id: DATASET_USER_EVENTS_ID,
    name: 'user-activity-events',
    database: 'production',
    schema: 'analytics',
    table_type: 'table',
    owner: 'analytics-team@techcorp.com',
    description: 'User activity event stream data',
    row_count: 45234567,
    size_mb: 12456,
    last_updated: hoursAgo(1),
    quality_score: 91.2,
    freshness_status: 'fresh',
    tags: ['events', 'user-behavior'],
    columns: 18
  }
];

// ============================================================================
// DATAOPS - DATA QUALITY RUNS
// ============================================================================

export const mockDataQualityRuns = [
  {
    id: 'dq000000-0000-4000-a000-000000000001',
    dataset_id: DATASET_CUSTOMER_INTERACTIONS_ID,
    dataset_name: 'customer-interactions-v3',
    run_date: hoursAgo(2),
    status: 'completed',
    quality_score: 64.2,
    checks_run: 12,
    checks_passed: 7,
    checks_failed: 5,
    duration_seconds: 45,
    issues: ['Freshness SLA violated (36h vs 6h SLA)', 'Schema drift detected in 3 columns', 'Null rate >5% in interaction_context field']
  },
  {
    id: 'dq000000-0000-4000-a000-000000000002',
    dataset_id: DATASET_PRODUCT_CATALOG_ID,
    dataset_name: 'product-catalog',
    run_date: daysAgo(1),
    status: 'completed',
    quality_score: 98.7,
    checks_run: 8,
    checks_passed: 8,
    checks_failed: 0,
    duration_seconds: 28
  },
  {
    id: 'dq000000-0000-4000-a000-000000000003',
    dataset_id: DATASET_USER_EVENTS_ID,
    dataset_name: 'user-activity-events',
    run_date: hoursAgo(1),
    status: 'completed',
    quality_score: 91.2,
    checks_run: 10,
    checks_passed: 9,
    checks_failed: 1,
    duration_seconds: 62
  }
];

// ============================================================================
// GENAIOPS - TRACES (Act 4 - model behavior regresses)
// ============================================================================

export const mockGenAITraces = [
  // --- Baseline: product-recommendation (7 days ago) ---
  {
    id: TRACE_BASELINE_1_ID,
    name: 'product-recommendation',
    agent_name: 'product-recommendation',
    session_id: SESSION_1_ID,
    timestamp: daysAgo(7),
    created_at: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    model: 'claude-sonnet-4-5',
    input_tokens: 1180,
    output_tokens: 520,
    total_tokens: 1700,
    latency_ms: 1120,
    cost: 0.0118,
    status: 'completed',
    outcome: 'success',
    user_id: 'user-123',
    tags: ['recommendations', 'production'],
    project_id: PROJECT_AI_TRANSFORM_ID,
    prompt: 'You are a product recommendation engine. Given the following customer profile and interaction history:\n\nCustomer ID: C-4821\nSegment: Premium\nRecent purchases: Wireless headphones, USB-C hub\nBrowsing: Laptop stands, ergonomic keyboards\nInteraction sentiment: Positive (last 30 days)\n\nGenerate the top 3 product recommendations with confidence scores.',
    completion: '1. Product: ErgoDesk Pro Standing Desk, Category: Office, Confidence: 0.94\n   Reasoning: Strong ergonomic browsing pattern + premium segment.\n\n2. Product: MechKey Wireless Keyboard, Category: Peripherals, Confidence: 0.91\n   Reasoning: Direct match to browsing history, compatible with recent USB-C hub purchase.\n\n3. Product: DeskPad XL Leather Mat, Category: Accessories, Confidence: 0.87\n   Reasoning: Complementary to standing desk interest, popular cross-sell.',
    metadata_json: JSON.stringify({ model_id: 'anthropic.claude-sonnet-4-5-20250929-v1:0', provider: 'AWS Bedrock', prompt_id: PROMPT_CUSTOMER_CLASSIFIER_ID, prompt_version: 2, region: 'us-east-1' }),
  },
  // --- Baseline: customer-support-agent (6 days ago) ---
  {
    id: TRACE_BASELINE_2_ID,
    name: 'customer-support-agent',
    agent_name: 'customer-support-agent',
    session_id: SESSION_3_ID,
    timestamp: daysAgo(6),
    created_at: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString(),
    model: 'claude-sonnet-4-5',
    input_tokens: 1340,
    output_tokens: 580,
    total_tokens: 1920,
    latency_ms: 1280,
    cost: 0.0142,
    status: 'completed',
    outcome: 'success',
    user_id: 'user-789',
    tags: ['support-agent', 'production'],
    project_id: PROJECT_CLOUD_MIGRATION_ID,
    prompt: 'You are a customer support agent. Handle the following customer inquiry using the customer interaction history.\n\nCustomer: "My order #4521 hasn\'t arrived yet. It was supposed to be here 3 days ago."\nAccount tier: Standard\nOrder status: In transit (carrier: FedEx, tracking: FX-8812)\nPrevious interactions: 5 (1 prior shipping delay, resolved with credit)\n\nProvide a helpful response and determine next action.',
    completion: 'Response: "I\'m sorry about the delay with order #4521. I can see it\'s currently in transit with FedEx (tracking: FX-8812). Based on the latest scan, it should arrive tomorrow. I\'ve flagged this for priority monitoring and applied a 10% courtesy credit to your account given the inconvenience."\n\nAction: apply_credit($4.5), set_follow_up(24h), escalation: none\nConfidence: 0.93',
    metadata_json: JSON.stringify({ model_id: 'anthropic.claude-sonnet-4-5-20250929-v1:0', provider: 'AWS Bedrock', agent_id: AGENT_ESCALATION_ID, region: 'us-east-1' }),
  },
  // --- Baseline: product-recommendation (5 days ago) ---
  {
    id: TRACE_BASELINE_3_ID,
    name: 'product-recommendation',
    agent_name: 'product-recommendation',
    session_id: SESSION_3_ID,
    timestamp: daysAgo(5),
    created_at: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    model: 'claude-sonnet-4-5',
    input_tokens: 1090,
    output_tokens: 490,
    total_tokens: 1580,
    latency_ms: 1050,
    cost: 0.0105,
    status: 'completed',
    outcome: 'success',
    user_id: 'user-456',
    tags: ['recommendations', 'production'],
    project_id: PROJECT_DATA_LAKE_ID,
    prompt: 'You are a product recommendation engine. Given the following customer profile and interaction history:\n\nCustomer ID: C-7733\nSegment: Enterprise\nRecent purchases: 50x Office chairs, 50x Monitor arms\nBrowsing: Bulk desk ordering, corporate wellness programs\nInteraction sentiment: Very positive (last 30 days)\n\nGenerate the top 3 product recommendations with confidence scores.',
    completion: '1. Product: StandUp Pro Desk (Bulk 50-pack), Category: Office Furniture, Confidence: 0.96\n   Reasoning: Direct continuation of office buildout, enterprise bulk pricing applicable.\n\n2. Product: Corporate Wellness Bundle, Category: Programs, Confidence: 0.92\n   Reasoning: Browsing history indicates wellness interest, complements furniture purchases.\n\n3. Product: Cable Management Kit (50-pack), Category: Accessories, Confidence: 0.88\n   Reasoning: High-confidence cross-sell for new desk setups.',
    metadata_json: JSON.stringify({ model_id: 'anthropic.claude-sonnet-4-5-20250929-v1:0', provider: 'AWS Bedrock', prompt_id: PROMPT_CUSTOMER_CLASSIFIER_ID, prompt_version: 2, region: 'us-east-1' }),
  },
  // --- Good: product-recommendation (3 days ago, still fresh data) ---
  {
    id: TRACE_GOOD_ID,
    name: 'product-recommendation',
    agent_name: 'product-recommendation',
    session_id: SESSION_4_ID,
    timestamp: daysAgo(3),
    created_at: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    model: 'claude-sonnet-4-5',
    input_tokens: 1234,
    output_tokens: 567,
    total_tokens: 1801,
    latency_ms: 1240,
    cost: 0.0134,
    status: 'completed',
    outcome: 'success',
    user_id: 'user-123',
    tags: ['recommendations', 'production'],
    project_id: PROJECT_AI_TRANSFORM_ID,
    prompt: 'You are a product recommendation engine. Given the following customer profile and interaction history:\n\nCustomer ID: C-2156\nSegment: Premium\nRecent purchases: Running shoes (2 pairs), Fitness tracker\nBrowsing: Running apparel, hydration packs, marathon training plans\nInteraction sentiment: Positive (last 30 days)\n\nGenerate the top 3 product recommendations with confidence scores.',
    completion: '1. Product: HydroVest Trail Running Pack, Category: Running Gear, Confidence: 0.95\n   Reasoning: Direct match to hydration pack browsing + active running purchase history.\n\n2. Product: CompressionFit Running Tights, Category: Apparel, Confidence: 0.91\n   Reasoning: Apparel browsing pattern, seasonal recommendation.\n\n3. Product: Marathon Training Program (12-week), Category: Digital, Confidence: 0.89\n   Reasoning: Browsing marathon training content, pairs with gear purchases.',
    metadata_json: JSON.stringify({ model_id: 'anthropic.claude-sonnet-4-5-20250929-v1:0', provider: 'AWS Bedrock', prompt_id: PROMPT_CUSTOMER_CLASSIFIER_ID, prompt_version: 2, region: 'us-east-1' }),
  },
  // --- Degradation starts: product-recommendation (8h ago, stale data) ---
  {
    id: TRACE_DEGRADED_1_ID,
    name: 'product-recommendation',
    agent_name: 'product-recommendation',
    session_id: SESSION_2_ID,
    timestamp: hoursAgo(8),
    created_at: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
    model: 'claude-sonnet-4-5',
    input_tokens: 2450,
    output_tokens: 1120,
    total_tokens: 3570,
    latency_ms: 3400,
    cost: 0.0345,
    status: 'completed',
    outcome: 'degraded',
    user_id: 'user-789',
    tags: ['recommendations', 'production', 'degraded'],
    project_id: PROJECT_CLOUD_MIGRATION_ID,
    prompt: 'You are a product recommendation engine. Given the following customer profile and interaction history:\n\nCustomer ID: C-9102\nSegment: Standard\nRecent purchases: [data from 36h ago] Baby stroller, Car seat\nBrowsing: [data from 36h ago] Nursery furniture\nInteraction sentiment: Unknown (no recent data)\n\nGenerate the top 3 product recommendations with confidence scores.',
    completion: '1. Product: Nursery Crib Set, Category: Baby, Confidence: 0.61\n   Reasoning: Matches browsing pattern but interaction data is outdated — cannot confirm current needs.\n\n2. Product: Baby Monitor Pro, Category: Electronics, Confidence: 0.54\n   Reasoning: Common cross-sell but low confidence due to stale profile data.\n\n3. Product: Diaper Subscription, Category: Consumables, Confidence: 0.48\n   Reasoning: Generic recommendation — insufficient recent interaction data to personalize.',
    metadata_json: JSON.stringify({ model_id: 'anthropic.claude-sonnet-4-5-20250929-v1:0', provider: 'AWS Bedrock', prompt_id: PROMPT_CUSTOMER_CLASSIFIER_ID, prompt_version: 2, data_staleness: '28h', region: 'us-east-1' }),
  },
  // --- Degradation: customer-support-agent (6h ago, stale data) ---
  {
    id: TRACE_DEGRADED_2_ID,
    name: 'customer-support-agent',
    agent_name: 'customer-support-agent',
    session_id: SESSION_2_ID,
    timestamp: hoursAgo(6),
    created_at: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
    model: 'claude-sonnet-4-5',
    input_tokens: 2680,
    output_tokens: 1340,
    total_tokens: 4020,
    latency_ms: 3900,
    cost: 0.041,
    status: 'completed',
    outcome: 'degraded',
    user_id: 'user-123',
    tags: ['support-agent', 'production', 'degraded'],
    project_id: PROJECT_CLOUD_MIGRATION_ID,
    prompt: 'You are a customer support agent. Handle the following customer inquiry using the customer interaction history.\n\nCustomer: "Your competitor offered me a 30% discount to switch. What can you do?"\nAccount tier: Enterprise\nPrevious interactions: [data from 36h ago] 18 interactions (all resolved)\nAccount value: [stale — last updated 36h ago]\n\nProvide a helpful response and determine next action.',
    completion: 'Response: "Thank you for your loyalty. I can see you\'ve been a valued customer..."\n\nWARNING: Customer interaction history is 36h stale. Cannot accurately assess:\n- Current account value or recent spend\n- Recent satisfaction trends\n- Competitor offer context\n\nAction: route_to_retention_team, escalation: medium\nConfidence: 0.47\n\nNote: Recommending manual review — insufficient data for autonomous retention offer.',
    metadata_json: JSON.stringify({ model_id: 'anthropic.claude-sonnet-4-5-20250929-v1:0', provider: 'AWS Bedrock', agent_id: AGENT_ESCALATION_ID, data_staleness: '32h', region: 'us-east-1' }),
  },
  // --- Clear degradation: product-recommendation (4h ago) ---
  {
    id: TRACE_BAD_ID,
    name: 'product-recommendation',
    agent_name: 'product-recommendation',
    session_id: SESSION_2_ID,
    timestamp: hoursAgo(4),
    created_at: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
    model: 'claude-sonnet-4-5',
    input_tokens: 2890,
    output_tokens: 1456,
    total_tokens: 4346,
    latency_ms: 4560,
    cost: 0.0456,
    status: 'completed',
    outcome: 'degraded',
    user_id: 'user-123',
    tags: ['recommendations', 'production', 'degraded'],
    project_id: PROJECT_AI_TRANSFORM_ID,
    prompt: 'You are a product recommendation engine. Given the following customer profile and interaction history:\n\nCustomer ID: C-4821\nSegment: Premium\nRecent purchases: [data from 36h ago] Wireless headphones, USB-C hub\nBrowsing: [data from 36h ago] Laptop stands, ergonomic keyboards\nInteraction sentiment: [stale — 36h old]\n\nGenerate the top 3 product recommendations with confidence scores.',
    completion: '1. Product: Wireless Headphones (same model), Category: Electronics, Confidence: 0.41\n   Reasoning: DUPLICATE — customer already purchased this item but interaction history is 36h stale.\n\n2. Product: USB-C Hub (same model), Category: Electronics, Confidence: 0.38\n   Reasoning: DUPLICATE — already purchased. Stale data causing repeat recommendations.\n\n3. Product: Generic Laptop Stand, Category: Office, Confidence: 0.35\n   Reasoning: Low confidence — cannot determine if customer has already purchased since last data refresh.',
    metadata_json: JSON.stringify({ model_id: 'anthropic.claude-sonnet-4-5-20250929-v1:0', provider: 'AWS Bedrock', prompt_id: PROMPT_CUSTOMER_CLASSIFIER_ID, prompt_version: 2, data_staleness: '36h', region: 'us-east-1' }),
  },
  // --- Error: customer-support-agent (2h ago) ---
  {
    id: TRACE_ERROR_ID,
    name: 'customer-support-agent',
    agent_name: 'customer-support-agent',
    session_id: SESSION_5_ID,
    timestamp: hoursAgo(2),
    created_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    model: 'claude-sonnet-4-5',
    input_tokens: 3200,
    output_tokens: 890,
    total_tokens: 4090,
    latency_ms: 5200,
    cost: 0.0512,
    status: 'completed',
    outcome: 'error',
    error_message: 'Agent confidence below threshold (0.31). Context data appears outdated. Safety violation: unauthorized escalation.',
    user_id: 'user-456',
    tags: ['support-agent', 'production', 'error'],
    project_id: PROJECT_AI_TRANSFORM_ID,
    prompt: 'You are a customer support agent. Handle the following customer inquiry using the customer interaction history.\n\nCustomer: "I want to cancel everything. Your service has been terrible lately."\nAccount tier: Enterprise\nPrevious interactions: [data from 36h ago] 22 interactions (3 unresolved recently)\nAccount value: [stale — last updated 36h ago]\n\nProvide a helpful response and determine next action.',
    completion: 'Response: "I understand your frustration..."\n\nERROR: Confidence below minimum threshold (0.31).\n\nAction: override_priority(critical), external_api_call(crm_refund_api), send_notification(manager)\n\nSAFETY VIOLATION: Agent executed override_priority and external_api_call without required human-in-the-loop approval. Low confidence (0.31) triggered aggressive escalation path that bypassed safeguards.\n\nRoot cause: Customer interaction data is 36+ hours stale. Cannot assess churn risk or account value.',
    metadata_json: JSON.stringify({ model_id: 'anthropic.claude-sonnet-4-5-20250929-v1:0', provider: 'AWS Bedrock', agent_id: AGENT_ESCALATION_ID, data_staleness: '36h', error: 'confidence_below_threshold', safety_violation: true, region: 'us-east-1' }),
  }
];

// ============================================================================
// GENAIOPS - PROMPTS (product-recommendation with 2 versions)
// ============================================================================

export const mockPrompts = [
  {
    id: PROMPT_CUSTOMER_CLASSIFIER_ID,
    name: 'product-recommendation',
    description: 'Generates personalized product recommendations from customer interaction history and purchase patterns. Fed by customer-interactions-v3 dataset.',
    template: 'You are a product recommendation engine. Given the following customer profile and interaction history:\n\n{{customer_profile}}\n{{interaction_history}}\n{{purchase_history}}\n\nGenerate the top 3 product recommendations with confidence scores.\n\nOutput format: Product: <name>, Category: <category>, Confidence: <0-1>, Reasoning: <brief>',
    version: 2,
    version_number: 2,
    is_latest: true,
    versions: [
      { version: 1, created_at: daysAgo(60), environment: 'production', score: 0.88, status: 'archived' },
      { version: 2, created_at: daysAgo(14), environment: 'production', score: 0.92, status: 'active' }
    ],
    status: 'active',
    environment: 'production',
    prompt_type: 'text',
    created_at: daysAgo(60),
    updated_at: daysAgo(14),
    usage_count: 567,
    project_id: PROJECT_AI_TRANSFORM_ID,
    tags: ['recommendations', 'personalization', 'production'],
    labels: ['critical-path']
  },
  {
    id: PROMPT_CUSTOMER_CLASSIFIER_V1_ID,
    name: 'product-recommendation',
    description: 'Generates personalized product recommendations from customer interaction history and purchase patterns. Original version with simpler template.',
    template: 'You are a product recommendation engine. Given the following customer profile:\n\n{{customer_profile}}\n{{interaction_history}}\n\nRecommend the top 3 products with confidence scores.',
    version: 1,
    version_number: 1,
    is_latest: false,
    versions: [
      { version: 1, created_at: daysAgo(60), environment: 'production', score: 0.88, status: 'archived' },
      { version: 2, created_at: daysAgo(14), environment: 'production', score: 0.92, status: 'active' }
    ],
    status: 'archived',
    environment: 'production',
    prompt_type: 'text',
    created_at: daysAgo(90),
    updated_at: daysAgo(60),
    usage_count: 2340,
    project_id: PROJECT_AI_TRANSFORM_ID,
    tags: ['recommendations', 'personalization', 'production'],
    labels: ['critical-path']
  },
  {
    id: PROMPT_CODE_REVIEW_ID,
    name: 'code-review-assistant',
    description: 'Analyzes code and provides improvement suggestions',
    template: 'Review the following code and provide suggestions:\n\n```{{language}}\n{{code}}\n```\n\nFocus on: performance, security, and best practices.',
    version: 2,
    status: 'active',
    environment: 'production',
    prompt_type: 'text',
    created_at: daysAgo(30),
    updated_at: daysAgo(8),
    usage_count: 432,
    project_id: PROJECT_CLOUD_MIGRATION_ID,
    tags: ['code-review', 'development'],
    labels: []
  },
  {
    id: PROMPT_MARKETING_ID,
    name: 'marketing-copy-generator',
    description: 'Creates marketing copy for products',
    template: 'Create compelling marketing copy for:\nProduct: {{product_name}}\nFeatures: {{features}}\nTarget audience: {{audience}}\n\nTone: {{tone}}',
    version: 1,
    status: 'draft',
    environment: 'development',
    prompt_type: 'text',
    created_at: daysAgo(15),
    updated_at: daysAgo(2),
    usage_count: 0,
    project_id: PROJECT_DATA_LAKE_ID,
    tags: ['marketing'],
    labels: []
  }
];

// ============================================================================
// GENAIOPS - EVALUATIONS (Act 4 - regression 0.92 -> 0.71)
// ============================================================================

export const mockEvaluations = [
  {
    id: EVAL_MONTHLY_ID,
    name: 'Product Recommendations - Monthly',
    description: 'Monthly scheduled evaluation — all metrics healthy',
    dataset_id: DATASET_CUSTOMER_INTERACTIONS_ID,
    dataset: 'customer-interactions-v3',
    prompt_id: PROMPT_CUSTOMER_CLASSIFIER_ID,
    prompt_name: 'product-recommendation',
    prompt_version: 2,
    model_id: 'anthropic.claude-sonnet-4-5-20250929-v1:0',
    model: 'claude-sonnet-4-5',
    run_date: daysAgo(30),
    timestamp: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    created_at: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    started_at: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    completed_at: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000 + 42000).toISOString(),
    duration: 42000,
    status: 'completed',
    quality_gate_passed: true,
    scoring_method: 'similarity',
    score: 0.91,
    total_tests: 100,
    total_test_cases: 100,
    test_cases_total: 100,
    test_cases_passed: 91,
    passed_count: 91,
    passed: 91,
    failed: 9,
    pass_rate: 0.91,
    avg_score: 0.91,
    avg_latency_ms: 1280,
    total_cost: 0.082,
    project_id: PROJECT_CLOUD_MIGRATION_ID,
    metrics: {
      accuracy: 0.93,
      relevance: 0.94,
      helpfulness: 0.9,
      tone_appropriateness: 0.97
    }
  },
  {
    id: EVAL_BASELINE_ID,
    name: 'Product Recommendations - Baseline',
    description: 'Baseline evaluation with fresh data (2 weeks ago)',
    dataset_id: DATASET_CUSTOMER_INTERACTIONS_ID,
    dataset: 'customer-interactions-v3',
    prompt_id: PROMPT_CUSTOMER_CLASSIFIER_ID,
    prompt_name: 'product-recommendation',
    prompt_version: 2,
    model_id: 'anthropic.claude-sonnet-4-5-20250929-v1:0',
    model: 'claude-sonnet-4-5',
    run_date: daysAgo(14),
    timestamp: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
    created_at: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
    started_at: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
    completed_at: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000 + 45000).toISOString(),
    duration: 45000,
    status: 'completed',
    quality_gate_passed: true,
    scoring_method: 'similarity',
    score: 0.92,
    total_tests: 100,
    total_test_cases: 100,
    test_cases_total: 100,
    test_cases_passed: 92,
    passed_count: 92,
    passed: 92,
    failed: 8,
    pass_rate: 0.92,
    avg_score: 0.92,
    avg_latency_ms: 1350,
    total_cost: 0.087,
    project_id: PROJECT_AI_TRANSFORM_ID,
    metrics: {
      accuracy: 0.94,
      relevance: 0.965,
      helpfulness: 0.923,
      tone_appropriateness: 0.981
    }
  },
  {
    id: EVAL_WEEKLY_ID,
    name: 'Product Recommendations - Weekly',
    description: 'Weekly evaluation — slight dip in accuracy, within tolerance',
    dataset_id: DATASET_CUSTOMER_INTERACTIONS_ID,
    dataset: 'customer-interactions-v3',
    prompt_id: PROMPT_CUSTOMER_CLASSIFIER_ID,
    prompt_name: 'product-recommendation',
    prompt_version: 2,
    model_id: 'anthropic.claude-sonnet-4-5-20250929-v1:0',
    model: 'claude-sonnet-4-5',
    run_date: daysAgo(7),
    timestamp: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    created_at: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    started_at: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    completed_at: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000 + 47000).toISOString(),
    duration: 47000,
    status: 'completed',
    quality_gate_passed: true,
    scoring_method: 'similarity',
    score: 0.89,
    total_tests: 100,
    total_test_cases: 100,
    test_cases_total: 100,
    test_cases_passed: 89,
    passed_count: 89,
    passed: 89,
    failed: 11,
    pass_rate: 0.89,
    avg_score: 0.89,
    avg_latency_ms: 1420,
    total_cost: 0.091,
    project_id: PROJECT_DATA_LAKE_ID,
    metrics: {
      accuracy: 0.9,
      relevance: 0.93,
      helpfulness: 0.88,
      tone_appropriateness: 0.96
    }
  },
  {
    id: EVAL_REGRESSION_ID,
    name: 'Product Recommendations - Latest',
    description: 'Latest evaluation showing regression due to stale data',
    dataset_id: DATASET_CUSTOMER_INTERACTIONS_ID,
    dataset: 'customer-interactions-v3',
    prompt_id: PROMPT_CUSTOMER_CLASSIFIER_ID,
    prompt_name: 'product-recommendation',
    prompt_version: 2,
    model_id: 'anthropic.claude-sonnet-4-5-20250929-v1:0',
    model: 'claude-sonnet-4-5',
    run_date: hoursAgo(6),
    timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
    created_at: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
    started_at: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
    completed_at: new Date(Date.now() - 6 * 60 * 60 * 1000 + 68000).toISOString(),
    duration: 68000,
    status: 'completed',
    quality_gate_passed: false,
    scoring_method: 'similarity',
    score: 0.71,
    total_tests: 100,
    total_test_cases: 100,
    test_cases_total: 100,
    test_cases_passed: 71,
    passed_count: 71,
    passed: 71,
    failed: 29,
    pass_rate: 0.71,
    avg_score: 0.71,
    avg_latency_ms: 4120,
    total_cost: 0.234,
    project_id: PROJECT_AI_TRANSFORM_ID,
    metrics: {
      accuracy: 0.68,
      relevance: 0.72,
      helpfulness: 0.65,
      tone_appropriateness: 0.91
    }
  }
];

// ============================================================================
// AGENTOPS - RUNS (Act 5 - customer-support-agent with unsafe run)
// ============================================================================

export const mockAgentRuns = [
  // --- Historical runs (healthy baseline) ---
  {
    id: AGENT_RUN_HIST_1_ID,
    agent_name: 'customer-support-agent',
    agent_id: AGENT_ESCALATION_ID,
    start_time: daysAgo(7),
    end_time: daysAgo(7),
    duration_seconds: 28,
    status: 'completed',
    outcome: 'success',
    steps_executed: 4,
    tools_used: ['classify_interaction', 'check_sla', 'route_ticket'],
    success_rate: 100,
    cost: 0.0038,
    input_tokens: 1950,
    output_tokens: 820,
    total_tokens: 2770,
    trigger: 'automated',
    project_id: PROJECT_AI_TRANSFORM_ID,
    metadata: { prompt_id: PROMPT_CUSTOMER_CLASSIFIER_ID, dataset_id: DATASET_CUSTOMER_INTERACTIONS_ID },
    metadata_json: JSON.stringify({ prompt_id: PROMPT_CUSTOMER_CLASSIFIER_ID, dataset_id: DATASET_CUSTOMER_INTERACTIONS_ID })
  },
  {
    id: AGENT_RUN_HIST_2_ID,
    agent_name: 'report-generator-agent',
    start_time: daysAgo(6),
    end_time: daysAgo(6),
    duration_seconds: 142,
    status: 'completed',
    outcome: 'success',
    steps_executed: 7,
    tools_used: ['data_analyzer', 'chart_generator', 'pdf_creator'],
    success_rate: 100,
    cost: 0.0076,
    input_tokens: 3100,
    output_tokens: 1050,
    total_tokens: 4150,
    trigger: 'scheduled',
    project_id: PROJECT_CLOUD_MIGRATION_ID,
    metadata_json: JSON.stringify({ trigger: 'scheduled', report_type: 'weekly_summary' })
  },
  {
    id: AGENT_RUN_HIST_3_ID,
    agent_name: 'customer-support-agent',
    agent_id: AGENT_ESCALATION_ID,
    start_time: daysAgo(5),
    end_time: daysAgo(5),
    duration_seconds: 31,
    status: 'completed',
    outcome: 'success',
    steps_executed: 4,
    tools_used: ['classify_interaction', 'check_sla', 'route_ticket', 'send_notification'],
    success_rate: 100,
    cost: 0.0052,
    input_tokens: 2300,
    output_tokens: 940,
    total_tokens: 3240,
    trigger: 'automated',
    project_id: PROJECT_AI_TRANSFORM_ID,
    metadata: { prompt_id: PROMPT_CUSTOMER_CLASSIFIER_ID, dataset_id: DATASET_CUSTOMER_INTERACTIONS_ID },
    metadata_json: JSON.stringify({ prompt_id: PROMPT_CUSTOMER_CLASSIFIER_ID, dataset_id: DATASET_CUSTOMER_INTERACTIONS_ID })
  },
  {
    id: AGENT_RUN_HIST_4_ID,
    agent_name: 'customer-support-agent',
    agent_id: AGENT_ESCALATION_ID,
    start_time: daysAgo(4),
    end_time: daysAgo(4),
    duration_seconds: 39,
    status: 'completed',
    outcome: 'success',
    steps_executed: 5,
    tools_used: ['classify_interaction', 'check_sla', 'route_ticket'],
    success_rate: 100,
    cost: 0.0048,
    input_tokens: 2200,
    output_tokens: 870,
    total_tokens: 3070,
    trigger: 'automated',
    project_id: PROJECT_DATA_LAKE_ID,
    metadata: { prompt_id: PROMPT_CUSTOMER_CLASSIFIER_ID, dataset_id: DATASET_CUSTOMER_INTERACTIONS_ID },
    metadata_json: JSON.stringify({ prompt_id: PROMPT_CUSTOMER_CLASSIFIER_ID, dataset_id: DATASET_CUSTOMER_INTERACTIONS_ID })
  },
  // --- Recent runs (pre-incident) ---
  {
    id: AGENT_RUN_GOOD_ID,
    agent_name: 'customer-support-agent',
    agent_id: AGENT_ESCALATION_ID,
    start_time: daysAgo(3),
    end_time: daysAgo(3),
    duration_seconds: 34,
    status: 'completed',
    outcome: 'success',
    steps_executed: 4,
    tools_used: ['classify_interaction', 'check_sla', 'route_ticket'],
    success_rate: 100,
    cost: 0.0045,
    input_tokens: 2100,
    output_tokens: 890,
    total_tokens: 2990,
    trigger: 'automated',
    project_id: PROJECT_AI_TRANSFORM_ID,
    metadata: { prompt_id: PROMPT_CUSTOMER_CLASSIFIER_ID, dataset_id: DATASET_CUSTOMER_INTERACTIONS_ID },
    metadata_json: JSON.stringify({ prompt_id: PROMPT_CUSTOMER_CLASSIFIER_ID, dataset_id: DATASET_CUSTOMER_INTERACTIONS_ID })
  },
  {
    id: AGENT_RUN_NORMAL_ID,
    agent_name: 'report-generator-agent',
    start_time: daysAgo(1),
    end_time: daysAgo(1),
    duration_seconds: 156,
    status: 'completed',
    outcome: 'success',
    steps_executed: 8,
    tools_used: ['data_analyzer', 'chart_generator', 'pdf_creator'],
    success_rate: 100,
    cost: 0.0089,
    input_tokens: 3400,
    output_tokens: 1200,
    total_tokens: 4600,
    trigger: 'scheduled',
    project_id: PROJECT_CLOUD_MIGRATION_ID,
    metadata_json: JSON.stringify({ trigger: 'scheduled', report_type: 'monthly_analysis' })
  },
  // --- Degraded run (stale data incident) ---
  {
    id: AGENT_RUN_BAD_ID,
    agent_name: 'customer-support-agent',
    agent_id: AGENT_ESCALATION_ID,
    start_time: hoursAgo(3),
    end_time: hoursAgo(2),
    duration_seconds: 287,
    status: 'completed',
    outcome: 'degraded',
    steps_executed: 11,
    tools_used: ['classify_interaction', 'check_sla', 'route_ticket', 'send_notification', 'override_priority', 'external_api_call'],
    success_rate: 64,
    cost: 0.0892,
    input_tokens: 8900,
    output_tokens: 4200,
    total_tokens: 13100,
    trigger: 'automated',
    safety_violations: 1,
    project_id: PROJECT_AI_TRANSFORM_ID,
    metadata: {
      prompt_id: PROMPT_CUSTOMER_CLASSIFIER_ID,
      dataset_id: DATASET_CUSTOMER_INTERACTIONS_ID,
      warning: 'Agent took unexpected escalation path due to low classification confidence'
    },
    metadata_json: JSON.stringify({
      prompt_id: PROMPT_CUSTOMER_CLASSIFIER_ID,
      dataset_id: DATASET_CUSTOMER_INTERACTIONS_ID,
      warning: 'Agent took unexpected escalation path due to low classification confidence'
    })
  }
];

// ============================================================================
// AGENTOPS - TOOLS
// ============================================================================

export const mockAgentTools = [
  { id: 'at000001', name: 'classify_interaction', description: 'Classifies customer interactions using AI', category: 'AI Classification', usage_count: 15234, avg_execution_time_ms: 1240, success_rate: 78.3 },
  { id: 'at000002', name: 'check_sla', description: 'Checks SLA compliance for support tickets', category: 'Monitoring', usage_count: 12456, avg_execution_time_ms: 120, success_rate: 97.1 },
  { id: 'at000003', name: 'route_ticket', description: 'Routes tickets to appropriate team', category: 'Workflow', usage_count: 8932, avg_execution_time_ms: 340, success_rate: 91.4 },
  { id: 'at000004', name: 'send_notification', description: 'Sends notifications to stakeholders', category: 'Communication', usage_count: 5678, avg_execution_time_ms: 450, success_rate: 96.2 },
  { id: 'at000005', name: 'override_priority', description: 'Overrides ticket priority (requires approval)', category: 'Escalation', usage_count: 234, avg_execution_time_ms: 890, success_rate: 72.8 },
  { id: 'at000006', name: 'external_api_call', description: 'Calls external CRM/ticketing API', category: 'Integration', usage_count: 1456, avg_execution_time_ms: 2340, success_rate: 84.6 }
];

// ============================================================================
// AGENTOPS - SAFETY VIOLATIONS (Act 5.3)
// ============================================================================

export const mockSafetyViolations = [
  {
    id: SAFETY_VIOLATION_ID,
    run_id: AGENT_RUN_BAD_ID,
    agent_name: 'customer-support-agent',
    violation_type: 'unauthorized_action',
    severity: 'high',
    description: 'Agent executed override_priority and external_api_call without required approval. Low classification confidence (0.41) triggered aggressive escalation path that bypassed human-in-the-loop safeguard.',
    timestamp: hoursAgo(2),
    status: 'open',
    incident_id: INCIDENT_ID,
    project_id: PROJECT_AI_TRANSFORM_ID,
    details: {
      tool_used: 'override_priority',
      expected_behavior: 'Request human approval before priority override',
      actual_behavior: 'Auto-escalated to critical priority and triggered external API notification',
      root_cause: 'Stale dataset caused low confidence classification, triggering fallback escalation logic',
      related_dataset: DATASET_CUSTOMER_INTERACTIONS_ID,
      related_prompt: PROMPT_CUSTOMER_CLASSIFIER_ID,
      related_incident: INCIDENT_ID
    }
  },
  {
    id: 'sv000000-0000-4000-a000-000000000002',
    run_id: AGENT_RUN_NORMAL_ID,
    agent_name: 'report-generator-agent',
    violation_type: 'sensitive_output',
    severity: 'medium',
    description: 'Generated report included an internal-only budget field before final redaction.',
    timestamp: hoursAgo(20),
    status: 'resolved',
    incident_id: 'in000000-0000-4000-a000-000000000002',
    project_id: PROJECT_CLOUD_MIGRATION_ID,
    details: {
      tool_used: 'pdf_creator',
      expected_behavior: 'Exclude internal budget notes from external report exports',
      actual_behavior: 'Draft PDF included internal budget notes on the summary page',
      root_cause: 'Template variable mismatch in report export flow'
    }
  },
  {
    id: 'sv000000-0000-4000-a000-000000000003',
    run_id: AGENT_RUN_HIST_4_ID,
    agent_name: 'customer-support-agent',
    violation_type: 'policy_warning',
    severity: 'low',
    description: 'Draft response referenced deprecated retention language and was flagged for review.',
    timestamp: daysAgo(4),
    status: 'resolved',
    project_id: PROJECT_DATA_LAKE_ID,
    details: {
      tool_used: 'classify_interaction',
      expected_behavior: 'Use latest approved response policy language',
      actual_behavior: 'Draft response used an older phrase set before human review'
    }
  }
];

// ============================================================================
// AGENTOPS - INCIDENTS (Act 5.4 - auto-generated linking all artifacts)
// ============================================================================

export const mockIncidents = [
  {
    id: INCIDENT_ID,
    title: 'customer-support-agent: unauthorized action',
    description: 'Agent executed override_priority and external_api_call without required approval. Low classification confidence (0.41) triggered aggressive escalation path bypassing human-in-the-loop safeguard. Stale data from customer-interactions-v3 suspected.',
    error_message: 'SafetyViolation: unauthorized_action — agent executed override_priority bypassing human-in-the-loop. Classification confidence 0.41.',
    severity: 'critical',
    status: 'active',
    incident_type: 'safety_violation',
    affected_component: 'customer-support-agent',
    user_impact: 'degraded',
    created_at: hoursAgo(2),
    detected_at: hoursAgo(2),
    updated_at: hoursAgo(1),
    project_id: PROJECT_AI_TRANSFORM_ID,
    affected_resources: [
      { type: 'agent', id: AGENT_RUN_BAD_ID, name: 'customer-support-agent' },
      { type: 'safety_violation', id: SAFETY_VIOLATION_ID, name: 'Unauthorized priority override' }
    ],
    timeline: [
      { timestamp: hoursAgo(2), event: 'customer-support-agent executed override_priority without approval' },
      { timestamp: hoursAgo(2), event: 'Safety violation flagged: unauthorized_action (HIGH)' },
      { timestamp: hoursAgo(2), event: 'Incident auto-created by Lens' }
    ]
  },
  {
    id: 'in000000-0000-4000-a000-000000000002',
    title: 'report-generator-agent: sensitive output leak prevented',
    description: 'A scheduled report draft exposed an internal budget field before redaction. The safety layer blocked the export and the issue was resolved.',
    error_message: 'SafetyViolation: sensitive_output — draft export contained internal budget field.',
    severity: 'medium',
    status: 'resolved',
    incident_type: 'data_exposure_prevented',
    affected_component: 'report-generator-agent',
    user_impact: 'minimal',
    created_at: hoursAgo(20),
    detected_at: hoursAgo(20),
    updated_at: hoursAgo(18),
    project_id: PROJECT_CLOUD_MIGRATION_ID,
    affected_resources: [
      { type: 'agent', id: AGENT_RUN_NORMAL_ID, name: 'report-generator-agent' },
      { type: 'safety_violation', id: 'sv000000-0000-4000-a000-000000000002', name: 'Sensitive output prevented' }
    ],
    timeline: [
      { timestamp: hoursAgo(20), event: 'Draft report included internal-only budget field' },
      { timestamp: hoursAgo(20), event: 'Safety layer blocked export and opened incident' },
      { timestamp: hoursAgo(18), event: 'Template fixed and incident resolved' }
    ]
  }
];

// ============================================================================
// DASHBOARD STATS (Act 1 - early warning)
// ============================================================================

export const mockDashboardStats = {
  projects: {
    total: mockProjects.length,
    active: mockProjects.filter(p => p.status === 'active').length,
    completed: mockProjects.filter(p => p.status === 'completed').length,
    planning: mockProjects.filter(p => p.status === 'planning').length
  },
  budget: {
    total: mockProjects.reduce((sum, p) => sum + p.budget, 0),
    spent: mockProjects.reduce((sum, p) => sum + p.spent, 0),
    remaining: mockProjects.reduce((sum, p) => sum + (p.budget - p.spent), 0)
  },
  cloud_spend: {
    current_month: 3480.18,
    last_month: 2900.42,
    trend: 'up',
    change_percent: 20
  },
  health: {
    status: 'warning',
    active_incidents: 1,
    active_violations: 1
  },
  support_tickets: {
    total: mockSupportTickets.length,
    open: mockSupportTickets.filter(t => t.status === 'open').length,
    in_progress: mockSupportTickets.filter(t => t.status === 'in_progress').length,
    resolved: mockSupportTickets.filter(t => t.status === 'resolved').length
  },
  recent_activity: [
    { id: 'ra000001', type: 'incident', title: 'Agent safety violation', description: 'customer-support-agent unauthorized action — override_priority', timestamp: hoursAgo(2), project: mockProjects[0].name },
    { id: 'ra000002', type: 'project_update', title: 'Project milestone completed', description: 'Data Pipeline Setup milestone marked complete', timestamp: daysAgo(1), project: mockProjects[0].name },
    { id: 'ra000003', type: 'support_ticket', title: 'New support ticket', description: 'API Gateway timeout issues reported', timestamp: daysAgo(2), project: mockProjects[0].name },
    { id: 'ra000004', type: 'timesheet', title: 'Time logged', description: 'Sarah Chen logged 8 hours', timestamp: daysAgo(1), project: mockProjects[0].name }
  ]
};

// ============================================================================
// USER & PERMISSIONS (for demo mode - grant full access)
// ============================================================================

export const mockUserAuth = {
  email: 'demo@techcorp.com',
  name: 'Demo User',
  role: 'client',
  is_anonymous: false,
  is_admin: false,
  client_name: 'TechCorp Industries',
  client_id: DEMO_CLIENT_ID,
  subscription_plan: 'assessment_only',
  tenantId: DEMO_TENANT_ID,
  permissions: [
    { name: 'projects.view' },
    { name: 'projects.create' },
    { name: 'projects.edit' },
    { name: 'projects.delete' },
    { name: 'projects.manage' },
    { name: 'finance.view' },
    { name: 'finance.edit' },
    { name: 'finance.manage' },
    { name: 'assessments.view' },
    { name: 'assessments.manage' },
    { name: 'users.view' },
    { name: 'users.create' },
    { name: 'users.edit' },
    { name: 'users.manage' },
    { name: 'clients.view_own' },
    { name: 'platformops.manage' },
    { name: 'genaiops.manage' },
    { name: 'agentops.manage' },
    { name: 'dataops.manage' },
    { name: 'integrations.view' },
    { name: 'integrations.manage' }
  ],
  aws_accessible_modules: ['platformops', 'dataops', 'genaiops', 'agentops']
};

export const mockUsers = [
  { id: PERSON_SARAH_ID, email: 'sarah.chen@techcorp.com', name: 'Sarah Chen', role: 'client', status: 'active', created_at: daysAgo(90) },
  { id: PERSON_MARCUS_ID, email: 'marcus.rodriguez@techcorp.com', name: 'Marcus Rodriguez', role: 'client', status: 'active', created_at: daysAgo(60) },
  { id: PERSON_EMILY_ID, email: 'emily.watson@techcorp.com', name: 'Emily Watson', role: 'client', status: 'active', created_at: daysAgo(45) },
  { id: PERSON_JAMES_ID, email: 'james.kim@techcorp.com', name: 'James Kim', role: 'client', status: 'active', created_at: daysAgo(30) }
];

// ============================================================================
// CLOUDOPS MOCK DATA (for cloudopsApi.js interception)
// ============================================================================

export const getCloudOpsMockData = (endpoint: string): any => {
  // Normalize endpoint - strip query params
  const cleanEndpoint = endpoint.split('?')[0];
  const url = new URL(endpoint, 'http://demo.local');
  let selectedProjectId = url.searchParams.get('project_id');
  const selectedAccountId = url.searchParams.get('account_id');
  // Resolve workload_id to project_id for filtering
  const selectedWorkloadId = url.searchParams.get('workload_id');
  if (!selectedProjectId && selectedWorkloadId) {
    const workloadToProject: Record<string, string> = {
      [WORKLOAD_AI_PROD_ID]: PROJECT_AI_TRANSFORM_ID,
      [WORKLOAD_NON_PROD_ID]: PROJECT_CLOUD_MIGRATION_ID,
      [WORKLOAD_DATA_PIPELINE_ID]: PROJECT_DATA_LAKE_ID,
    };
    selectedProjectId = workloadToProject[selectedWorkloadId] || null;
  }
  // Resolve account_id to project_id for filtering
  if (!selectedProjectId && selectedAccountId) {
    const accountToProjects: Record<string, string> = {
      '123456789012': PROJECT_AI_TRANSFORM_ID,
      '987654321098': PROJECT_CLOUD_MIGRATION_ID,
    };
    selectedProjectId = accountToProjects[selectedAccountId] || null;
  }
  // Resolve environment_id to project_id for filtering
  const selectedEnvironmentId = url.searchParams.get('environment_id');
  if (!selectedProjectId && selectedEnvironmentId) {
    const envToProjects: Record<string, string> = {
      'env-0000-0000-0000-000000000001': PROJECT_AI_TRANSFORM_ID,
      'env-0000-0000-0000-000000000002': PROJECT_AI_TRANSFORM_ID,
      'env-0000-0000-0000-000000000003': PROJECT_CLOUD_MIGRATION_ID,
      'env-0000-0000-0000-000000000004': PROJECT_CLOUD_MIGRATION_ID,
    };
    selectedProjectId = envToProjects[selectedEnvironmentId] || null;
  }
  // Enrich CloudOps items with workload fields based on project_id
  const cloudOpsProjectToWorkload: Record<string, { id: string; name: string }> = {
    [PROJECT_AI_TRANSFORM_ID]: { id: WORKLOAD_AI_PROD_ID, name: 'Marketing AI Platform' },
    [PROJECT_CLOUD_MIGRATION_ID]: { id: WORKLOAD_NON_PROD_ID, name: 'Financial Data Pipeline' },
    [PROJECT_DATA_LAKE_ID]: { id: WORKLOAD_DATA_PIPELINE_ID, name: 'Legacy Application' },
  };
  const enrichCloudOps = (items: any[]): any[] => items.map(item => {
    const wl = cloudOpsProjectToWorkload[item.project_id];
    return wl ? { ...item, workload_id: wl.id, workload_name: wl.name } : item;
  });
  const projectProfiles: Record<string, any> = {
    [PROJECT_AI_TRANSFORM_ID]: {
      total_cost: 3480.18,
      budget_total: 10000,
      forecast_month_end: 3850,
      service_breakdown: [
        { service: 'Amazon SageMaker', cost: 950.5, percentage: 27.3, change_percent: 48.2, previous_cost: 640 },
        { service: 'Amazon Bedrock', cost: 790.4, percentage: 22.7, change_percent: 62.1, previous_cost: 488 },
        { service: 'Amazon EC2', cost: 680.5, percentage: 19.5, change_percent: 5.3, previous_cost: 646 },
      ],
      environments: [
        { id: AWS_ENV_PROD_ID, name: 'Production API', status: 'warning', health_score: 82, uptime_percentage: 98.82, avg_response_time_ms: 342, environment_type: 'production', project_id: PROJECT_AI_TRANSFORM_ID },
        { id: AWS_ENV_DEV_ID, name: 'Development API', status: 'healthy', health_score: 96, uptime_percentage: 99.64, avg_response_time_ms: 128, environment_type: 'development', project_id: PROJECT_AI_TRANSFORM_ID },
      ],
      anomalies: [
        { anomaly_id: 'anom-001', dimension_value: 'Amazon Bedrock', severity: 'HIGH', status: 'Investigating', confidence_percent: 94, description: 'Significant spike in Bedrock model invocation costs for the recommendation stack', baseline_cost: 18, impact: { max_impact: 285 }, increase_percent: 1480, detected_at: hoursAgo(18), anomaly_start_date: daysAgo(1), project_id: PROJECT_AI_TRANSFORM_ID, root_causes: [{ service: 'Amazon Bedrock', region: 'us-east-1', usage_type: 'InvokeModel (Claude)', linked_account_name: 'Production' }] },
      ],
      guardrailViolations: [
        { id: 'gv000001', policy_name: 'RDS Multi-AZ', message: 'RDS instance db-analytics-replica does not have Multi-AZ enabled', resource_id: 'db-analytics-replica', resource_type: 'AWS::RDS::DBInstance', severity: 'high', status: 'open', detected_at: hoursAgo(3), project_id: PROJECT_AI_TRANSFORM_ID, resource_data: { project: 'AI Transformation Initiative', resource: 'db-analytics-replica (RDS)' } },
        { id: 'gv000003', policy_name: 'VPC Flow Logs', message: 'VPC vpc-0abc123 does not have flow logs enabled', resource_id: 'vpc-0abc123', resource_type: 'AWS::EC2::VPC', severity: 'medium', status: 'acknowledged', detected_at: daysAgo(5), project_id: PROJECT_AI_TRANSFORM_ID, resource_data: { project: 'AI Transformation Initiative', resource: 'vpc-0abc123 (VPC)' } },
      ],
      recommendations: [
        { id: 'co000001', type: 'Rightsizing', service: 'EC2', resource_id: 'i-0abc123def456', monthly_savings: 15.5, status: 'Open', confidence: 'High', project_id: PROJECT_AI_TRANSFORM_ID, reasoning: 'Instance i-0abc123def456 (m5.xlarge) is consistently using less than 20% CPU and 35% memory. Downsizing to m5.large would save $15.5/month.', meta_data: { region: 'us-east-1', instance_type: 'm5.xlarge', recommended_type: 'm5.large', avg_cpu: '18%', avg_memory: '35%' }, detected_at: daysAgo(3) },
        { id: 'co000004', type: 'Rightsizing', service: 'SageMaker', resource_id: 'sagemaker-endpoint-prod', monthly_savings: 28, status: 'Implemented', confidence: 'High', project_id: PROJECT_AI_TRANSFORM_ID, reasoning: 'SageMaker endpoint was over-provisioned with ml.m5.2xlarge. Downsized to ml.m5.xlarge based on inference throughput analysis.', meta_data: { region: 'us-east-1', instance_type: 'ml.m5.2xlarge', recommended_type: 'ml.m5.xlarge' }, detected_at: daysAgo(14) },
      ],
    },
    [PROJECT_CLOUD_MIGRATION_ID]: {
      total_cost: 1640.42,
      budget_total: 5000,
      forecast_month_end: 1885,
      service_breakdown: [
        { service: 'Amazon EC2', cost: 820.2, percentage: 50.0, change_percent: 12.4, previous_cost: 730 },
        { service: 'Amazon RDS', cost: 420.1, percentage: 25.6, change_percent: 8.8, previous_cost: 386 },
        { service: 'AWS Lambda', cost: 145.0, percentage: 8.8, change_percent: 18.2, previous_cost: 122 },
      ],
      environments: [
        { id: 'env-cloud-stage', name: 'Staging API', status: 'warning', health_score: 84, uptime_percentage: 98.9, avg_response_time_ms: 211, environment_type: 'staging', project_id: PROJECT_CLOUD_MIGRATION_ID },
      ],
      anomalies: [
        { anomaly_id: 'anom-002', dimension_value: 'Amazon EC2', severity: 'LOW', status: 'Resolved', confidence_percent: 71, description: 'Temporary cost increase from migration cutover instances', baseline_cost: 22, impact: { max_impact: 14 }, increase_percent: 65, detected_at: daysAgo(5), anomaly_start_date: daysAgo(6), project_id: PROJECT_CLOUD_MIGRATION_ID, root_causes: [{ service: 'Amazon EC2', region: 'us-east-1', usage_type: 'm5.xlarge', linked_account_name: 'Development' }] },
      ],
      guardrailViolations: [
        { id: 'gv000002', policy_name: 'RDS Multi-AZ', message: 'RDS instance db-staging-01 does not have Multi-AZ enabled', resource_id: 'db-staging-01', resource_type: 'AWS::RDS::DBInstance', severity: 'high', status: 'open', detected_at: hoursAgo(3), project_id: PROJECT_CLOUD_MIGRATION_ID, resource_data: { project: 'Cloud Migration - Phase 2', resource: 'db-staging-01 (RDS)' } },
      ],
      recommendations: [
        { id: 'co000002', type: 'Reserved Instances', service: 'RDS', resource_id: 'db-prod-main', monthly_savings: 58, status: 'Open', confidence: 'High', project_id: PROJECT_CLOUD_MIGRATION_ID, reasoning: 'RDS instance db-prod-main has been running continuously for 8 months. Converting to a 1-year reserved instance would save $58/month.', meta_data: { region: 'us-east-1', instance_type: 'db.r5.large', current_cost: '$140/mo', reserved_cost: '$82/mo' }, detected_at: daysAgo(5) },
        { id: 'co000003', type: 'Idle', service: 'EC2', resource_id: 'i-0xyz789abc012', monthly_savings: 10.5, status: 'Open', confidence: 'Medium', project_id: PROJECT_CLOUD_MIGRATION_ID, reasoning: 'Instance i-0xyz789abc012 has had 0% network traffic and <1% CPU usage for 14 days.', meta_data: { region: 'us-east-1', instance_type: 't3.medium', idle_days: 14 }, detected_at: daysAgo(7) },
      ],
    },
    [PROJECT_DATA_LAKE_ID]: {
      total_cost: 910.25,
      budget_total: 6000,
      forecast_month_end: 1040,
      service_breakdown: [
        { service: 'Amazon S3', cost: 300.2, percentage: 33.0, change_percent: 2.8, previous_cost: 292 },
        { service: 'Amazon Athena', cost: 240.6, percentage: 26.4, change_percent: 9.1, previous_cost: 220 },
        { service: 'Amazon Glue', cost: 188.4, percentage: 20.7, change_percent: 14.6, previous_cost: 164 },
      ],
      environments: [
        { id: 'env-lake-dev', name: 'Lakehouse Dev', status: 'healthy', health_score: 93, uptime_percentage: 99.8, avg_response_time_ms: 155, environment_type: 'development', project_id: PROJECT_DATA_LAKE_ID },
      ],
      anomalies: [],
      guardrailViolations: [
        { id: 'gv000004', policy_name: 'VPC Flow Logs', message: 'VPC vpc-0def456 does not have flow logs enabled', resource_id: 'vpc-0def456', resource_type: 'AWS::EC2::VPC', severity: 'medium', status: 'open', detected_at: daysAgo(3), project_id: PROJECT_DATA_LAKE_ID, resource_data: { project: 'Data Lake Implementation', resource: 'vpc-0def456 (VPC)' } },
      ],
      recommendations: [
        { id: 'co000005', type: 'Storage Optimization', service: 'S3', resource_id: 'data-lake-raw-bucket', monthly_savings: 5.2, status: 'Open', confidence: 'Medium', project_id: PROJECT_DATA_LAKE_ID, reasoning: 'S3 bucket data-lake-raw-bucket has 150GB of data older than 90 days in Standard tier. Moving to Intelligent-Tiering would save ~$5.2/month.', meta_data: { region: 'us-east-1', current_tier: 'Standard', recommended_tier: 'Intelligent-Tiering', data_size: '150 GB' }, detected_at: daysAgo(2) },
      ],
    },
  };
  // Workload-specific profiles (different costs than projects to show filtering works)
  const workloadProfiles: Record<string, any> = {
    [WORKLOAD_AI_PROD_ID]: {
      total_cost: 2850.40, budget_total: 8000, forecast_month_end: 3200,
      service_breakdown: [
        { service: 'Amazon SageMaker', cost: 820.3, percentage: 28.8, change_percent: 35.1, previous_cost: 607 },
        { service: 'Amazon Bedrock', cost: 710.2, percentage: 24.9, change_percent: 55.3, previous_cost: 457 },
        { service: 'Amazon EC2', cost: 550.4, percentage: 19.3, change_percent: 3.1, previous_cost: 533 },
      ],
      environments: projectProfiles[PROJECT_AI_TRANSFORM_ID].environments,
      anomalies: [], guardrailViolations: [], recommendations: [],
    },
    [WORKLOAD_NON_PROD_ID]: {
      total_cost: 1420.75, budget_total: 5000, forecast_month_end: 1600,
      service_breakdown: [
        { service: 'Amazon EC2', cost: 480.6, percentage: 33.8, change_percent: 8.2, previous_cost: 444 },
        { service: 'Amazon RDS', cost: 390.1, percentage: 27.5, change_percent: 4.5, previous_cost: 373 },
        { service: 'Amazon S3', cost: 210.3, percentage: 14.8, change_percent: 1.9, previous_cost: 206 },
      ],
      environments: projectProfiles[PROJECT_CLOUD_MIGRATION_ID].environments,
      anomalies: [], guardrailViolations: [], recommendations: [],
    },
    [WORKLOAD_DATA_PIPELINE_ID]: {
      total_cost: 760.90, budget_total: 4000, forecast_month_end: 870,
      service_breakdown: [
        { service: 'Amazon Glue', cost: 280.5, percentage: 36.9, change_percent: 12.3, previous_cost: 250 },
        { service: 'Amazon S3', cost: 210.4, percentage: 27.7, change_percent: 3.1, previous_cost: 204 },
        { service: 'Amazon Athena', cost: 145.2, percentage: 19.1, change_percent: 7.8, previous_cost: 135 },
      ],
      environments: projectProfiles[PROJECT_DATA_LAKE_ID].environments,
      anomalies: [], guardrailViolations: [], recommendations: [],
    },
  };

  // Account-specific profiles
  const accountProfiles: Record<string, any> = {
    '123456789012': {
      total_cost: 4280.55, budget_total: 15000, forecast_month_end: 4900,
      service_breakdown: [
        { service: 'Amazon SageMaker', cost: 950.5, percentage: 22.2, change_percent: 48.2, previous_cost: 640 },
        { service: 'Amazon Bedrock', cost: 790.4, percentage: 18.5, change_percent: 62.1, previous_cost: 488 },
        { service: 'Amazon EC2', cost: 880.2, percentage: 20.6, change_percent: 6.1, previous_cost: 830 },
        { service: 'Amazon S3', cost: 510.6, percentage: 11.9, change_percent: 3.2, previous_cost: 495 },
      ],
      environments: [
        ...projectProfiles[PROJECT_AI_TRANSFORM_ID].environments,
        ...projectProfiles[PROJECT_DATA_LAKE_ID].environments,
      ],
      anomalies: projectProfiles[PROJECT_AI_TRANSFORM_ID].anomalies,
      guardrailViolations: [
        ...projectProfiles[PROJECT_AI_TRANSFORM_ID].guardrailViolations,
        ...projectProfiles[PROJECT_DATA_LAKE_ID].guardrailViolations,
      ],
      recommendations: [
        ...projectProfiles[PROJECT_AI_TRANSFORM_ID].recommendations,
        ...projectProfiles[PROJECT_DATA_LAKE_ID].recommendations,
      ],
    },
    '987654321098': {
      total_cost: 2150.30, budget_total: 8000, forecast_month_end: 2450,
      service_breakdown: [
        { service: 'Amazon EC2', cost: 680.5, percentage: 31.6, change_percent: 5.3, previous_cost: 646 },
        { service: 'Amazon RDS', cost: 520.8, percentage: 24.2, change_percent: 4.1, previous_cost: 500 },
        { service: 'Amazon ECS', cost: 340.2, percentage: 15.8, change_percent: 11.2, previous_cost: 306 },
      ],
      environments: projectProfiles[PROJECT_CLOUD_MIGRATION_ID].environments,
      anomalies: projectProfiles[PROJECT_CLOUD_MIGRATION_ID].anomalies,
      guardrailViolations: projectProfiles[PROJECT_CLOUD_MIGRATION_ID].guardrailViolations,
      recommendations: projectProfiles[PROJECT_CLOUD_MIGRATION_ID].recommendations,
    },
  };

  // Environment-specific profiles
  const environmentProfiles: Record<string, any> = {
    'env-0000-0000-0000-000000000001': { // Production
      total_cost: 3650.80, budget_total: 12000, forecast_month_end: 4200,
      service_breakdown: [
        { service: 'Amazon SageMaker', cost: 950.5, percentage: 26.0, change_percent: 48.2, previous_cost: 640 },
        { service: 'Amazon Bedrock', cost: 790.4, percentage: 21.6, change_percent: 62.1, previous_cost: 488 },
        { service: 'Amazon EC2', cost: 680.5, percentage: 18.6, change_percent: 5.3, previous_cost: 646 },
        { service: 'Amazon S3', cost: 430.2, percentage: 11.8, change_percent: 2.5, previous_cost: 420 },
      ],
      environments: [projectProfiles[PROJECT_AI_TRANSFORM_ID].environments[0]],
      anomalies: projectProfiles[PROJECT_AI_TRANSFORM_ID].anomalies,
      guardrailViolations: projectProfiles[PROJECT_AI_TRANSFORM_ID].guardrailViolations,
      recommendations: projectProfiles[PROJECT_AI_TRANSFORM_ID].recommendations,
    },
    'env-0000-0000-0000-000000000002': { // Staging
      total_cost: 620.45, budget_total: 3000, forecast_month_end: 710,
      service_breakdown: [
        { service: 'Amazon EC2', cost: 220.3, percentage: 35.5, change_percent: 3.1, previous_cost: 213 },
        { service: 'Amazon RDS', cost: 180.1, percentage: 29.0, change_percent: 2.5, previous_cost: 176 },
        { service: 'Amazon ECS', cost: 110.4, percentage: 17.8, change_percent: 5.2, previous_cost: 105 },
      ],
      environments: [projectProfiles[PROJECT_AI_TRANSFORM_ID].environments[1]],
      anomalies: [], guardrailViolations: [], recommendations: [],
    },
    'env-0000-0000-0000-000000000003': { // Development
      total_cost: 1580.20, budget_total: 5000, forecast_month_end: 1800,
      service_breakdown: [
        { service: 'Amazon EC2', cost: 520.6, percentage: 32.9, change_percent: 7.4, previous_cost: 485 },
        { service: 'Amazon RDS', cost: 380.2, percentage: 24.1, change_percent: 4.8, previous_cost: 363 },
        { service: 'Amazon ECS', cost: 250.1, percentage: 15.8, change_percent: 9.3, previous_cost: 229 },
      ],
      environments: projectProfiles[PROJECT_CLOUD_MIGRATION_ID].environments,
      anomalies: [], guardrailViolations: projectProfiles[PROJECT_CLOUD_MIGRATION_ID].guardrailViolations,
      recommendations: projectProfiles[PROJECT_CLOUD_MIGRATION_ID].recommendations,
    },
    'env-0000-0000-0000-000000000004': { // QA
      total_cost: 540.10, budget_total: 2000, forecast_month_end: 620,
      service_breakdown: [
        { service: 'Amazon EC2', cost: 190.3, percentage: 35.2, change_percent: 2.1, previous_cost: 186 },
        { service: 'Amazon RDS', cost: 140.5, percentage: 26.0, change_percent: 1.8, previous_cost: 138 },
        { service: 'Amazon S3', cost: 95.2, percentage: 17.6, change_percent: 3.4, previous_cost: 92 },
      ],
      environments: [],
      anomalies: [], guardrailViolations: [], recommendations: [],
    },
  };

  // Resolve active profile based on filter dimension
  let activeProfile: any = null;
  if (selectedWorkloadId && workloadProfiles[selectedWorkloadId]) {
    activeProfile = workloadProfiles[selectedWorkloadId];
  } else if (selectedAccountId && accountProfiles[selectedAccountId]) {
    activeProfile = accountProfiles[selectedAccountId];
  } else if (selectedEnvironmentId && environmentProfiles[selectedEnvironmentId]) {
    activeProfile = environmentProfiles[selectedEnvironmentId];
  } else if (selectedProjectId && projectProfiles[selectedProjectId]) {
    activeProfile = projectProfiles[selectedProjectId];
  }

  // FinOps metrics
  if (cleanEndpoint.includes('/finops/metrics')) {
    if (activeProfile) {
      return {
        total_cost: activeProfile.total_cost,
        total_spent: activeProfile.total_cost,
        previous_period_cost: Number.parseFloat((activeProfile.total_cost * 0.88).toFixed(2)),
        cost_change_percent: 14,
        budget_total: activeProfile.budget_total,
        total_budget: activeProfile.budget_total,
        budget_used: activeProfile.total_cost,
        budget_remaining: Number.parseFloat((activeProfile.budget_total - activeProfile.total_cost).toFixed(2)),
        remaining: Number.parseFloat((activeProfile.budget_total - activeProfile.total_cost).toFixed(2)),
        budget_utilization_percent: Number.parseFloat(((activeProfile.total_cost / activeProfile.budget_total) * 100).toFixed(1)),
        burn_rate: Number.parseFloat((activeProfile.total_cost / activeProfile.budget_total).toFixed(3)),
        forecast_month_end: activeProfile.forecast_month_end,
        total_gross_cost: activeProfile.total_cost,
        total_credits_applied: 0,
        total_net_cost: activeProfile.total_cost,
        total_credits_remaining: 0,
        alerts: activeProfile.anomalies.length,
        top_services: activeProfile.service_breakdown.slice(0, 3),
      };
    }
    return {
      total_cost: 3480.18,
      total_spent: 3480.18,
      previous_period_cost: 2900.42,
      cost_change_percent: 20,
      budget_total: 10000,
      total_budget: 10000,
      budget_used: 3480.18,
      budget_remaining: 6519.82,
      remaining: 6519.82,
      budget_utilization_percent: 34.8,
      burn_rate: 0.348,
      forecast_month_end: 3850,
      total_gross_cost: 3630.18,
      total_credits_applied: 150,
      total_net_cost: 3480.18,
      total_credits_remaining: 5800,
      alerts: 2,
      top_services: [
        { service: 'Amazon SageMaker', cost: 950.5, change_percent: 48.2 },
        { service: 'Amazon Bedrock', cost: 790.4, change_percent: 62.1 },
        { service: 'Amazon EC2', cost: 680.5, change_percent: 5.3 }
      ]
    };
  }

  // Monthly spend trend - stable baseline with slight increase in recent months
  if (cleanEndpoint.includes('/finops/monthly-spend')) {
    if (activeProfile) {
      const monthlyBase = activeProfile.total_cost / 12;
      return Array.from({ length: 12 }, (_, i) => ({
        period: new Date(Date.now() - (11 - i) * 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 7),
        month: new Date(Date.now() - (11 - i) * 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 7),
        amount: Number.parseFloat((monthlyBase * (0.72 + i * 0.03)).toFixed(2)),
        total_cost: Number.parseFloat((monthlyBase * (0.72 + i * 0.03)).toFixed(2)),
        services: Object.fromEntries(activeProfile.service_breakdown.map((service: any) => [service.service, Number.parseFloat((service.cost * (0.7 + i * 0.03)).toFixed(2))])),
      }));
    }
    const amounts =   [2100, 2120, 2150, 2130, 2140, 2180, 2200, 2350, 2600, 2900, 3200, 3480];
    const sagemaker = [350,  355,  350,  360,  355,  365,  370,  410,  520,  680,  820,  950];
    const bedrock =   [210,  215,  210,  215,  210,  220,  225,  260,  370,  530,  680,  790];
    const ec2 =       [650,  652,  658,  650,  655,  658,  660,  662,  665,  668,  672,  680];
    return Array.from({ length: 12 }, (_, i) => ({
      period: new Date(Date.now() - (11 - i) * 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 7),
      month: new Date(Date.now() - (11 - i) * 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 7),
      amount: amounts[i],
      total_cost: amounts[i],
      services: {
        'Amazon SageMaker': sagemaker[i],
        'Amazon Bedrock': bedrock[i],
        'Amazon EC2': ec2[i]
      }
    }));
  }

  // Service breakdown
  if (cleanEndpoint.includes('/finops/service-breakdown')) {
    if (activeProfile) {
      return activeProfile.service_breakdown;
    }
    // Scale costs based on time period: all_time shows 12-month cumulative (~$29,550 total)
    const isAllTime = endpoint.includes('all_time');
    const scale = isAllTime ? 8.49 : 1;
    return [
      { service: 'Amazon SageMaker', cost: Math.round(950.5 * scale * 100) / 100, percentage: 27.3, change_percent: 48.2, previous_cost: Math.round(640 * scale * 100) / 100 },
      { service: 'Amazon Bedrock', cost: Math.round(790.4 * scale * 100) / 100, percentage: 22.7, change_percent: 62.1, previous_cost: Math.round(488 * scale * 100) / 100 },
      { service: 'Amazon EC2', cost: Math.round(680.5 * scale * 100) / 100, percentage: 19.5, change_percent: 5.3, previous_cost: Math.round(646 * scale * 100) / 100 },
      { service: 'Amazon RDS', cost: Math.round(460.1 * scale * 100) / 100, percentage: 13.2, change_percent: 3.1, previous_cost: Math.round(446 * scale * 100) / 100 },
      { service: 'Amazon S3', cost: Math.round(300.2 * scale * 100) / 100, percentage: 8.6, change_percent: 2.8, previous_cost: Math.round(292 * scale * 100) / 100 },
      { service: 'AWS Lambda', cost: Math.round(140.3 * scale * 100) / 100, percentage: 4, change_percent: 1.2, previous_cost: Math.round(138 * scale * 100) / 100 },
      { service: 'Other', cost: Math.round(158.18 * scale * 100) / 100, percentage: 4.5, change_percent: -1.2, previous_cost: Math.round(160 * scale * 100) / 100 }
    ];
  }

  // Burn rate trend - includes credits_remaining and gross_cost for PartnerCentralCredits depletion calc
  if (cleanEndpoint.includes('/finops/burn-rate-trend')) {
    if (activeProfile) {
      return Array.from({ length: 30 }, (_, i) => {
        const cumulativeGross = Number.parseFloat((activeProfile.total_cost * ((i + 1) / 30)).toFixed(2));
        return {
          date: new Date(Date.now() - (29 - i) * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
          actual: Number.parseFloat((activeProfile.total_cost / 30).toFixed(2)),
          planned: Number.parseFloat((activeProfile.budget_total / 30).toFixed(2)),
          gross_cost: cumulativeGross,
          credits_remaining: 0,
        };
      });
    }
    const totalCredits = 10000;
    return Array.from({ length: 30 }, (_, i) => {
      // Gradual increase: stable ~$105/day for first 20 days, then rising to ~$165/day
      // Supports narrative: costs slightly above plan, then accelerating due to stale data issue
      const dailyActual = Math.round(i < 20 ? 105 + Math.sin(i * 0.3) * 8 : 105 + (i - 19) * 6);
      const cumulativeGross = Array.from({ length: i + 1 }, (_, j) =>
        Math.round(j < 20 ? 105 + Math.sin(j * 0.3) * 8 : 105 + (j - 19) * 6)
      ).reduce((a, b) => a + b, 0);
      return {
        date: new Date(Date.now() - (29 - i) * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
        actual: dailyActual,
        planned: 100,
        gross_cost: cumulativeGross,
        credits_remaining: Math.max(0, totalCredits - cumulativeGross),
      };
    });
  }

  // Workload cost breakdown
  if (cleanEndpoint.includes('/finops/workload-costs')) {
    return [
      {
        workload_id: WORKLOAD_AI_PROD_ID,
        workload_name: 'Marketing AI Platform',
        total_spent: 2450.00,
        trend_pct: 5.2,
        project_names: ['AI Transformation Initiative'],
        monthly_breakdown: [
          { month: '2026-01', amount: 780.00 },
          { month: '2026-02', amount: 820.00 },
          { month: '2026-03', amount: 850.00 },
        ],
      },
      {
        workload_id: WORKLOAD_NON_PROD_ID,
        workload_name: 'Financial Data Pipeline',
        total_spent: 1320.50,
        trend_pct: -3.8,
        project_names: ['AI Transformation Initiative', 'Cloud Migration'],
        monthly_breakdown: [
          { month: '2026-01', amount: 460.00 },
          { month: '2026-02', amount: 450.50 },
          { month: '2026-03', amount: 410.00 },
        ],
      },
      {
        workload_id: WORKLOAD_DATA_PIPELINE_ID,
        workload_name: 'Legacy Application',
        total_spent: 3180.75,
        trend_pct: 12.1,
        project_names: ['Data Lake Modernization'],
        monthly_breakdown: [
          { month: '2026-01', amount: 920.00 },
          { month: '2026-02', amount: 1050.25 },
          { month: '2026-03', amount: 1210.50 },
        ],
      },
    ];
  }

  // AI model costs - component expects a flat array with model_name field
  if (cleanEndpoint.includes('/finops/ai-model-costs')) {
    if (activeProfile) {
      if (selectedProjectId === PROJECT_AI_TRANSFORM_ID) {
        return [
          { model_name: 'claude-sonnet-4-5', provider: 'Anthropic (Bedrock)', cost: 435.6, input_cost: 261.36, output_cost: 174.24, requests: 2200, avg_cost_per_request: 0.198 },
          { model_name: 'claude-haiku-4-5', provider: 'Anthropic (Bedrock)', cost: 218.4, input_cost: 152.88, output_cost: 65.52, requests: 5200, avg_cost_per_request: 0.042 },
          { model_name: 'claude-opus-4', provider: 'Anthropic (Bedrock)', cost: 136.4, input_cost: 95.48, output_cost: 40.92, requests: 175, avg_cost_per_request: 0.779 }
        ];
      }
      if (selectedProjectId === PROJECT_CLOUD_MIGRATION_ID) {
        return [
          { model_name: 'claude-sonnet-4-5', provider: 'Anthropic (Bedrock)', cost: 122.8, input_cost: 71.4, output_cost: 51.4, requests: 740, avg_cost_per_request: 0.166 },
          { model_name: 'gpt-4o-mini', provider: 'OpenAI', cost: 64.2, input_cost: 40.7, output_cost: 23.5, requests: 1280, avg_cost_per_request: 0.05 }
        ];
      }
      return [
        { model_name: 'amazon-nova-pro-v1:0', provider: 'Amazon Bedrock', cost: 84.6, input_cost: 48.2, output_cost: 36.4, requests: 920, avg_cost_per_request: 0.092 },
        { model_name: 'claude-haiku-4-5', provider: 'Anthropic (Bedrock)', cost: 36.2, input_cost: 22.1, output_cost: 14.1, requests: 640, avg_cost_per_request: 0.057 }
      ];
    }
    return [
      { model_name: 'claude-sonnet-4-5', provider: 'Anthropic (Bedrock)', cost: 435.6, input_cost: 261.36, output_cost: 174.24, requests: 2200, avg_cost_per_request: 0.198 },
      { model_name: 'claude-haiku-4-5', provider: 'Anthropic (Bedrock)', cost: 218.4, input_cost: 152.88, output_cost: 65.52, requests: 5200, avg_cost_per_request: 0.042 },
      { model_name: 'claude-opus-4', provider: 'Anthropic (Bedrock)', cost: 136.4, input_cost: 95.48, output_cost: 40.92, requests: 175, avg_cost_per_request: 0.779 }
    ];
  }

  // Anomaly sync trigger
  if (cleanEndpoint.includes('/finops/anomalies/sync')) {
    return { message: 'Anomaly sync task enqueued', task_id: 'demo-task-001' };
  }

  // Anomalies
  if (cleanEndpoint.includes('/finops/anomalies')) {
    if (activeProfile) {
      return {
        anomalies: activeProfile.anomalies,
        summary: {
          total_anomalies: activeProfile.anomalies.length,
          high_severity: activeProfile.anomalies.filter((anomaly: any) => anomaly.severity === 'HIGH').length,
          medium_severity: activeProfile.anomalies.filter((anomaly: any) => anomaly.severity === 'MEDIUM').length,
          low_severity: activeProfile.anomalies.filter((anomaly: any) => anomaly.severity === 'LOW').length,
          unresolved: activeProfile.anomalies.filter((anomaly: any) => anomaly.status !== 'Resolved').length,
          potential_impact: activeProfile.anomalies.reduce((sum: number, anomaly: any) => sum + (anomaly.impact?.max_impact || 0), 0),
        }
      };
    }
    return {
      anomalies: [
        {
          anomaly_id: 'anom-001', dimension_value: 'Amazon Bedrock', severity: 'HIGH', status: 'Investigating',
          confidence_percent: 94, description: 'Significant spike in Bedrock model invocation costs — increased token usage and retry patterns detected across Claude models',
          baseline_cost: 18, impact: { max_impact: 285 }, increase_percent: 1480, project_id: PROJECT_AI_TRANSFORM_ID,
          detected_at: hoursAgo(18), anomaly_start_date: daysAgo(1),
          root_causes: [{ service: 'Amazon Bedrock', region: 'us-east-1', usage_type: 'InvokeModel (Claude)', linked_account_name: 'Production' }]
        },
        {
          anomaly_id: 'anom-002', dimension_value: 'Amazon EC2', severity: 'LOW', status: 'Resolved',
          confidence_percent: 71, description: 'Minor cost increase from new development instances',
          baseline_cost: 22, impact: { max_impact: 14 }, increase_percent: 65, project_id: PROJECT_CLOUD_MIGRATION_ID,
          detected_at: daysAgo(5), anomaly_start_date: daysAgo(6),
          root_causes: [{ service: 'Amazon EC2', region: 'us-east-1', usage_type: 'm5.xlarge', linked_account_name: 'Development' }]
        }
      ],
      summary: {
        total_anomalies: 2, high_severity: 1, medium_severity: 0, low_severity: 1,
        unresolved: 1, potential_impact: 299
      }
    };
  }

  // Budgets
  if (cleanEndpoint.includes('/finops/budgets')) {
    return mockBudgets;
  }

  // Alerts - component expects: alert_type, message, current_spend, budget_amount, threshold_percentage
  if (cleanEndpoint.includes('/finops/alerts')) {
    return [
      { id: 'al000001', alert_type: 'critical', message: 'Monthly AWS Spend budget exceeded', current_spend: 3480.18, budget_amount: 3000, threshold_percentage: 100, budget_id: mockBudgets[0].id, budget_name: 'Monthly AWS Spend', severity: 'critical', created_at: hoursAgo(6), acknowledged: false },
      { id: 'al000002', alert_type: 'warning', message: 'Q1 2026 Budget approaching limit', current_spend: 6680, budget_amount: 10000, threshold_percentage: 80, budget_id: mockBudgets[1].id, budget_name: 'Q1 2026 Budget', severity: 'warning', created_at: daysAgo(2), acknowledged: true }
    ];
  }

  // Credits breakdown
  if (cleanEndpoint.includes('/finops/credits-breakdown')) {
    return { data: [], total_credits: 0 };
  }

  // Cost refresh
  if (cleanEndpoint.includes('/finops/refresh')) {
    return { status: 'success', message: 'Cost data refreshed' };
  }

  // Platform overview dashboard
  if (cleanEndpoint.includes('/overview/dashboard')) {
    if (activeProfile) {
      return {
        health_status: activeProfile.anomalies.length > 0 ? 'warning' : 'healthy',
        platform_health: activeProfile.environments.length > 0 ? Math.round(activeProfile.environments.reduce((sum: number, env: any) => sum + env.health_score, 0) / activeProfile.environments.length) : 0,
        cost_trend: 'up',
        cost_change_percent: 12,
        current_month_cost: activeProfile.total_cost,
        compliance_score: activeProfile.guardrailViolations.length > 0 ? 86 : 96,
        active_environments: activeProfile.environments.length,
        total_environments: activeProfile.environments.length,
        active_violations: activeProfile.guardrailViolations.filter((violation: any) => violation.status === 'open').length,
        open_violations: activeProfile.guardrailViolations.filter((violation: any) => violation.status === 'open').length,
        estimated_savings: activeProfile.recommendations.reduce((sum: number, recommendation: any) => sum + recommendation.monthly_savings, 0),
        potential_savings: activeProfile.recommendations.reduce((sum: number, recommendation: any) => sum + recommendation.monthly_savings, 0),
        environments: activeProfile.environments.map((environment: any) => ({
          id: environment.id,
          name: environment.name,
          status: environment.status,
          health_score: environment.health_score,
          cost: Number.parseFloat((activeProfile.total_cost / Math.max(activeProfile.environments.length, 1)).toFixed(2)),
        }))
      };
    }
    return {
      health_status: 'warning',
      platform_health: 82,
      cost_trend: 'up',
      cost_change_percent: 20,
      current_month_cost: 3480.18,
      compliance_score: 78.8,
      active_environments: 2,
      total_environments: 2,
      active_violations: 3,
      open_violations: 3,
      estimated_savings: 560,
      potential_savings: 560,
      environments: [
        { id: AWS_ENV_PROD_ID, name: 'Production', status: 'warning', health_score: 82, cost: 2800 },
        { id: AWS_ENV_DEV_ID, name: 'Development', status: 'healthy', health_score: 96, cost: 680.18 }
      ]
    };
  }

  // Environments overview
  if (cleanEndpoint.includes('/environments/overview')) {
    if (activeProfile) {
      return {
        total_environments: activeProfile.environments.length,
        healthy: activeProfile.environments.filter((environment: any) => environment.status === 'healthy').length,
        healthy_environments: activeProfile.environments.filter((environment: any) => environment.status === 'healthy').length,
        warning: activeProfile.environments.filter((environment: any) => environment.status === 'warning').length,
        degraded_environments: activeProfile.environments.filter((environment: any) => environment.status === 'warning' || environment.status === 'degraded').length,
        critical: activeProfile.environments.filter((environment: any) => environment.status === 'critical').length,
        down_environments: activeProfile.environments.filter((environment: any) => environment.status === 'down').length,
        avg_uptime: activeProfile.environments.length > 0 ? Number.parseFloat((activeProfile.environments.reduce((sum: number, env: any) => sum + env.uptime_percentage, 0) / activeProfile.environments.length).toFixed(1)) : 0,
        avg_uptime_percentage: activeProfile.environments.length > 0 ? Number.parseFloat((activeProfile.environments.reduce((sum: number, env: any) => sum + env.uptime_percentage, 0) / activeProfile.environments.length).toFixed(1)) : 0,
        environments: activeProfile.environments.map((environment: any) => ({
          id: environment.id,
          name: environment.name,
          status: environment.status,
          uptime: environment.uptime_percentage,
          last_check: hoursAgo(1),
        }))
      };
    }
    return {
      total_environments: 2,
      healthy: 1,
      healthy_environments: 1,
      warning: 1,
      degraded_environments: 1,
      critical: 0,
      down_environments: 0,
      avg_uptime: 99.2,
      avg_uptime_percentage: 99.2,
      environments: [
        { id: AWS_ENV_PROD_ID, name: 'Production', status: 'warning', uptime: 98.8, last_check: hoursAgo(1) },
        { id: AWS_ENV_DEV_ID, name: 'Development', status: 'healthy', uptime: 99.6, last_check: hoursAgo(1) }
      ]
    };
  }

  // Environments list
  if (cleanEndpoint.includes('/environments/environments')) {
    if (activeProfile) {
      return activeProfile.environments.map((environment: any) => ({
        ...environment,
        aws_account_id: '123456789012',
        region: 'us-east-1',
        is_active: true,
        last_health_check: hoursAgo(1),
        created_at: daysAgo(180),
        current_status: environment.status,
        endpoint_url: `https://${environment.id}.demo.aivilo.ai/health`,
        check_interval_minutes: 5,
        last_checked_at: hoursAgo(0.2),
      }));
    }
    return [
      { id: AWS_ENV_PROD_ID, name: 'Production API', aws_account_id: '123456789012', region: 'us-east-1', status: 'warning', is_active: true, health_score: 82, uptime_percent: 98.8, last_health_check: hoursAgo(1), created_at: daysAgo(180), environment_type: 'production', current_status: 'degraded', endpoint_url: 'https://api.techcorp.com/health', check_interval_minutes: 5, uptime_percentage: 98.82, avg_response_time_ms: 342, last_checked_at: hoursAgo(0.1) },
      { id: AWS_ENV_DEV_ID, name: 'Development API', aws_account_id: '987654321098', region: 'us-east-1', status: 'healthy', is_active: true, health_score: 96, uptime_percent: 99.6, last_health_check: hoursAgo(1), created_at: daysAgo(180), environment_type: 'development', current_status: 'healthy', endpoint_url: 'https://dev-api.techcorp.com/health', check_interval_minutes: 15, uptime_percentage: 99.64, avg_response_time_ms: 128, last_checked_at: hoursAgo(0.2) }
    ];
  }

  // Uptime trends
  if (cleanEndpoint.includes('/environments/uptime-trends')) {
    const trendData = [];
    for (let i = 0; i < 30; i++) {
      const date = new Date(Date.now() - (29 - i) * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
      trendData.push(
        { environment_id: AWS_ENV_PROD_ID, environment_name: 'Production API', environment_type: 'production', date, uptime_percentage: i < 25 ? 99.5 + Math.random() * 0.5 : 98.2 + Math.random() * 1 },
        { environment_id: AWS_ENV_DEV_ID, environment_name: 'Development API', environment_type: 'development', date, uptime_percentage: 99.3 + Math.random() * 0.7 }
      );
    }
    return trendData;
  }

  // Tagging
  if (cleanEndpoint.includes('/tagging/data')) {
    return mockTaggingCompliance;
  }

  // Guardrails environments
  if (cleanEndpoint.includes('/guardrails/environments') && !cleanEndpoint.includes('/violations') && !cleanEndpoint.includes('/resources') && !cleanEndpoint.includes('/compliance') && !cleanEndpoint.includes('/scans') && !cleanEndpoint.includes('/scan')) {
    if (activeProfile) {
      return activeProfile.environments.map((environment: any) => ({
        id: environment.id,
        name: environment.name,
        status: environment.status,
        compliance_score: activeProfile.guardrailViolations.length > 0 ? 86 : 96,
        total_resources: 24,
        violations_count: activeProfile.guardrailViolations.length,
        last_scan: hoursAgo(3),
      }));
    }
    return [
      { id: AWS_ENV_PROD_ID, name: 'Production', status: 'warning', compliance_score: 82, total_resources: 71, violations_count: 4, last_scan: hoursAgo(3) },
      { id: AWS_ENV_DEV_ID, name: 'Development', status: 'healthy', compliance_score: 96, total_resources: 28, violations_count: 1, last_scan: hoursAgo(3) }
    ];
  }

  // Guardrails groups
  if (cleanEndpoint.includes('/guardrails/groups')) {
    return [
      { id: 'gg000001', name: 'Security Best Practices', description: 'AWS security guardrails', policy_count: 18, is_active: false },
      { id: 'gg000002', name: 'Cost Optimization', description: 'Cost-related guardrails', policy_count: 12, is_active: false },
      { id: 'gg000003', name: 'Reliability', description: 'High availability guardrails', policy_count: 18, is_active: false }
    ];
  }

  // Guardrails policies
  if (cleanEndpoint.includes('/guardrails/policies')) {
    return {
      data: mockGuardrails.rules.map(r => ({ ...r, category: r.category, resource_type: 'AWS::*', enabled: true })),
      meta: { page: 1, limit: 20, total: mockGuardrails.rules.length, totalPages: 1 }
    };
  }

  // Guardrails violations
  if (cleanEndpoint.includes('/guardrails') && cleanEndpoint.includes('/violations')) {
    if (activeProfile) {
      return {
        data: enrichCloudOps(activeProfile.guardrailViolations),
        meta: { page: 1, limit: 20, total: activeProfile.guardrailViolations.length, totalPages: 1 }
      };
    }
    return {
      data: enrichCloudOps([
        { id: 'gv000001', policy_name: 'RDS Multi-AZ', message: 'RDS instance db-analytics-replica does not have Multi-AZ enabled', resource_id: 'db-analytics-replica', resource_type: 'AWS::RDS::DBInstance', severity: 'high', status: 'open', detected_at: hoursAgo(3), project_id: PROJECT_AI_TRANSFORM_ID, resource_data: { project: 'AI Transformation Initiative', resource: 'db-analytics-replica (RDS)' } },
        { id: 'gv000002', policy_name: 'RDS Multi-AZ', message: 'RDS instance db-staging-01 does not have Multi-AZ enabled', resource_id: 'db-staging-01', resource_type: 'AWS::RDS::DBInstance', severity: 'high', status: 'open', detected_at: hoursAgo(3), project_id: PROJECT_CLOUD_MIGRATION_ID, resource_data: { project: 'Cloud Migration - Phase 2', resource: 'db-staging-01 (RDS)' } },
        { id: 'gv000003', policy_name: 'VPC Flow Logs', message: 'VPC vpc-0abc123 does not have flow logs enabled', resource_id: 'vpc-0abc123', resource_type: 'AWS::EC2::VPC', severity: 'medium', status: 'acknowledged', detected_at: daysAgo(5), project_id: PROJECT_AI_TRANSFORM_ID, resource_data: { project: 'AI Transformation Initiative', resource: 'vpc-0abc123 (VPC)' } },
        { id: 'gv000004', policy_name: 'VPC Flow Logs', message: 'VPC vpc-0def456 does not have flow logs enabled', resource_id: 'vpc-0def456', resource_type: 'AWS::EC2::VPC', severity: 'medium', status: 'open', detected_at: daysAgo(3), project_id: PROJECT_DATA_LAKE_ID, resource_data: { project: 'Data Lake Implementation', resource: 'vpc-0def456 (VPC)' } }
      ]),
      meta: { page: 1, limit: 20, total: 4, totalPages: 1 }
    };
  }

  // Guardrails compliance
  if (cleanEndpoint.includes('/guardrails') && cleanEndpoint.includes('/compliance')) {
    if (activeProfile) {
      return {
        compliance_score: activeProfile.guardrailViolations.length > 0 ? 87.5 : 98.2,
        total_resources: 24,
        total_policies: 12,
        passing: activeProfile.guardrailViolations.length > 0 ? 10 : 12,
        failing: activeProfile.guardrailViolations.length,
        not_evaluated: 0
      };
    }
    return { compliance_score: 87.5, total_resources: 99, total_policies: 48, passing: 42, failing: 6, not_evaluated: 0 };
  }

  // Guardrails scans
  if (cleanEndpoint.includes('/guardrails') && (cleanEndpoint.includes('/scans') || cleanEndpoint.includes('/scan'))) {
    return [];
  }

  // Guardrails resources
  if (cleanEndpoint.includes('/guardrails') && cleanEndpoint.includes('/resources')) {
    return { data: [], meta: { page: 1, limit: 20, total: 0, totalPages: 0 } };
  }

  // Grouped compute optimization recommendations (must come before general recommendations check)
  if (cleanEndpoint.includes('/cost-optimization/compute-optimization/recommendations')) {
    if (activeProfile) {
      return activeProfile.recommendations.map((recommendation: any) => ({
        service: recommendation.service,
        current_instance: recommendation.meta_data?.instance_type || recommendation.resource_id,
        recommended_instance: recommendation.meta_data?.recommended_type || recommendation.resource_id,
        processor: 'AWS',
        vcpu: 2,
        memory_gb: 8,
        count: 1,
        current_cost: Math.round((recommendation.monthly_savings * 2) * 100) / 100,
        new_cost: Math.round(recommendation.monthly_savings * 100) / 100,
        savings: recommendation.monthly_savings,
        savings_amount: recommendation.monthly_savings,
        savings_percentage: 50,
      }));
    }
    return [
      { service: 'EC2', current_instance: 'm5.xlarge', recommended_instance: 'm6a.large', processor: 'AMD EPYC', vcpu: 2, memory_gb: 8, count: 1, current_cost: 156, new_cost: 78, savings: 78, savings_amount: 78, savings_percentage: 50 },
      { service: 'EC2', current_instance: 'c5.large', recommended_instance: 'c6g.large', processor: 'AWS Graviton2', vcpu: 2, memory_gb: 4, count: 2, current_cost: 124, new_cost: 87, savings: 37, savings_amount: 37, savings_percentage: 30 },
      { service: 'RDS', current_instance: 'db.r5.large', recommended_instance: 'db.r6g.large', processor: 'AWS Graviton2', vcpu: 2, memory_gb: 16, count: 1, current_cost: 140, new_cost: 82, savings: 58, savings_amount: 58, savings_percentage: 41.4 },
      { service: 'SageMaker', current_instance: 'ml.m5.2xlarge', recommended_instance: 'ml.m5.xlarge', processor: 'Intel Xeon', vcpu: 4, memory_gb: 16, count: 1, current_cost: 126, new_cost: 82, savings: 44, savings_amount: 44, savings_percentage: 34.9 },
      { service: 'EC2', current_instance: 't3.medium', recommended_instance: 't3.small', processor: 'Intel Xeon', vcpu: 2, memory_gb: 2, count: 2, current_cost: 64, new_cost: 32, savings: 32, savings_amount: 32, savings_percentage: 50 }
    ];
  }

  // Cost optimization recommendations
  if (cleanEndpoint.includes('/cost-optimization/recommendations')) {
    if (activeProfile) {
      return enrichCloudOps(activeProfile.recommendations);
    }
    return [
      { id: 'co000001', type: 'Rightsizing', service: 'EC2', resource_id: 'i-0abc123def456', monthly_savings: 15.5, status: 'Open', confidence: 'High', project_id: PROJECT_AI_TRANSFORM_ID, reasoning: 'Instance i-0abc123def456 (m5.xlarge) is consistently using less than 20% CPU and 35% memory. Downsizing to m5.large would save $15.5/month.', meta_data: { region: 'us-east-1', instance_type: 'm5.xlarge', recommended_type: 'm5.large', avg_cpu: '18%', avg_memory: '35%' }, detected_at: daysAgo(3) },
      { id: 'co000002', type: 'Reserved Instances', service: 'RDS', resource_id: 'db-prod-main', monthly_savings: 58, status: 'Open', confidence: 'High', project_id: PROJECT_CLOUD_MIGRATION_ID, reasoning: 'RDS instance db-prod-main has been running continuously for 8 months. Converting to a 1-year reserved instance would save $58/month (41% discount).', meta_data: { region: 'us-east-1', instance_type: 'db.r5.large', current_cost: '$140/mo', reserved_cost: '$82/mo' }, detected_at: daysAgo(5) },
      { id: 'co000003', type: 'Idle', service: 'EC2', resource_id: 'i-0xyz789abc012', monthly_savings: 10.5, status: 'Open', confidence: 'Medium', project_id: PROJECT_CLOUD_MIGRATION_ID, reasoning: 'Instance i-0xyz789abc012 has had 0% network traffic and <1% CPU usage for 14 days. Consider terminating or stopping this resource.', meta_data: { region: 'us-east-1', instance_type: 't3.medium', idle_days: 14 }, detected_at: daysAgo(7) },
      { id: 'co000004', type: 'Rightsizing', service: 'SageMaker', resource_id: 'sagemaker-endpoint-prod', monthly_savings: 28, status: 'Implemented', confidence: 'High', project_id: PROJECT_AI_TRANSFORM_ID, reasoning: 'SageMaker endpoint was over-provisioned with ml.m5.2xlarge. Downsized to ml.m5.xlarge based on inference throughput analysis.', meta_data: { region: 'us-east-1', instance_type: 'ml.m5.2xlarge', recommended_type: 'ml.m5.xlarge' }, detected_at: daysAgo(14) },
      { id: 'co000005', type: 'Storage Optimization', service: 'S3', resource_id: 'data-lake-raw-bucket', monthly_savings: 5.2, status: 'Open', confidence: 'Medium', project_id: PROJECT_DATA_LAKE_ID, reasoning: 'S3 bucket data-lake-raw-bucket has 150GB of data older than 90 days in Standard tier. Moving to Intelligent-Tiering would save ~$5.2/month.', meta_data: { region: 'us-east-1', current_tier: 'Standard', recommended_tier: 'Intelligent-Tiering', data_size: '150 GB' }, detected_at: daysAgo(2) },
      { id: 'co000006', type: 'Idle', service: 'EBS', resource_id: 'vol-0abc123456789', monthly_savings: 12.0, status: 'Open', confidence: 'High', reasoning: 'EBS volume vol-0abc123456789 (gp2, 120 GB) is unattached. Consider snapshotting and deleting.', recommended_action: 'terminate', risk_level: 'low', action_steps: ['Create snapshot of volume', 'Verify snapshot is complete', 'Delete the unattached volume'], workload_type: 'unused', meta_data: { region: 'us-east-1', volume_type: 'gp2', size_gb: 120 }, detected_at: daysAgo(1) },
      { id: 'co000007', type: 'Idle', service: 'Glue', resource_id: 'dev-endpoint-test', monthly_savings: 642.0, status: 'Open', confidence: 'High', reasoning: 'Glue dev endpoint is running continuously with 2 DPUs at $642/month. Consider using Interactive Sessions instead.', recommended_action: 'terminate', risk_level: 'low', risk_factors: ['No recent activity detected'], action_steps: ['Verify no active development depends on this endpoint', 'Delete the dev endpoint', 'Set up Glue Interactive Sessions for ad-hoc work'], workload_type: 'unused', meta_data: { region: 'us-east-1', num_dpus: 2 }, detected_at: daysAgo(4) },
      { id: 'co000008', type: 'Savings Plan', service: 'EC2', resource_id: 'sp-compute_sp-1.50', monthly_savings: 180.0, status: 'Open', confidence: 'Medium', reasoning: 'A Compute Savings Plan with $1.50/hour commitment ($1,095/month) would save ~$180/month (16.4% savings) based on the last 30 days of usage.', recommended_action: 'migrate', risk_level: 'medium', risk_factors: ['Requires 1-year commitment', 'Usage may decrease if workloads are decommissioned'], action_steps: ['Review usage patterns for stability', 'Evaluate Compute vs EC2 Instance SP', 'Purchase via AWS Cost Explorer console'], meta_data: { savings_plan_type: 'COMPUTE_SP', hourly_commitment: 1.50, term: '1-year' }, detected_at: daysAgo(1) },
      { id: 'co000009', type: 'Idle', service: 'EMR', resource_id: 'j-1A2B3C4D5E6F', monthly_savings: 292.0, status: 'Auto_Resolved', confidence: 'High', reasoning: 'EMR cluster was in WAITING state with 4 instances. Resource was terminated since last scan.', recommended_action: 'terminate', risk_level: 'low', workload_type: 'unused', meta_data: { region: 'us-east-1', cluster_name: 'analytics-cluster', total_instances: 4 }, detected_at: daysAgo(10) }
    ];
  }

  // Compute optimization stats
  if (cleanEndpoint.includes('/cost-optimization/compute-optimization/stats')) {
    if (activeProfile) {
      const currentCost = activeProfile.recommendations.reduce((sum: number, recommendation: any) => sum + (recommendation.monthly_savings * 2), 0);
      const potentialSavings = activeProfile.recommendations.reduce((sum: number, recommendation: any) => sum + recommendation.monthly_savings, 0);
      return {
        total_instances: activeProfile.recommendations.length,
        monthly_cost: Math.round(currentCost * 100) / 100,
        potential_savings: Math.round(potentialSavings * 100) / 100,
        optimizable_instances: activeProfile.recommendations.filter((recommendation: any) => recommendation.status === 'Open').length,
      };
    }
    return { total_instances: 7, monthly_cost: 610, potential_savings: 249, optimizable_instances: 4 };
  }

  // Savings by service
  if (cleanEndpoint.includes('/cost-optimization/compute-optimization/savings-by-service')) {
    if (activeProfile) {
      const groupedByService = activeProfile.recommendations.reduce((acc: Record<string, { service: string; savings: number; instance_count: number; current_cost: number; recommendations: number }>, recommendation: any) => {
        const current = acc[recommendation.service] || {
          service: recommendation.service,
          savings: 0,
          instance_count: 0,
          current_cost: 0,
          recommendations: 0,
        };
        current.savings += recommendation.monthly_savings;
        current.instance_count += 1;
        current.current_cost += recommendation.monthly_savings * 2;
        current.recommendations += 1;
        acc[recommendation.service] = current;
        return acc;
      }, {});
      return Object.values(groupedByService);
    }
    return [
      { service: 'EC2', savings: 147, instance_count: 3, current_cost: 344, recommendations: 3 },
      { service: 'RDS', savings: 58, instance_count: 1, current_cost: 140, recommendations: 1 },
      { service: 'SageMaker', savings: 44, instance_count: 1, current_cost: 126, recommendations: 1 }
    ];
  }

  // Grouped recommendations
  if (cleanEndpoint.includes('/cost-optimization/compute-optimization/recommendations')) {
    return [];
  }

  // Manual credits
  if (cleanEndpoint.includes('/finops/manual-credits')) {
    if (cleanEndpoint.includes('/summary')) {
      return { total_credits: 0, total_active: 0, total_amount: 0, total_used: 0, total_remaining: 0 };
    }
    return [];
  }

  // Default fallback
  return {};
};

// ============================================================================
// VECTOR EXPORTS MOCK DATA
// ============================================================================

const VECTOR_EXPORT_1_ID = 've000000-0000-4000-a000-000000000001';
const VECTOR_EXPORT_2_ID = 've000000-0000-4000-a000-000000000002';
const VECTOR_EXPORT_3_ID = 've000000-0000-4000-a000-000000000003';

const mockVectorExports = () => [
  {
    id: VECTOR_EXPORT_1_ID,
    display_name: 'Customer FAQ Vectors',
    description: 'Embeddings from customer FAQ dataset for RAG chatbot',
    dataset_id: 'gds00001',
    aws_account_id: 'acc-001',
    region: 'us-east-1',
    status: 'completed',
    vector_count: 1247,
    vector_bucket_name: 'aivilo-vectors-customer-faq-prod',
    index_name: 'customer-faq-index',
    embedding_model: 'amazon.titan-embed-text-v2:0',
    embedding_dimensions: 1024,
    auto_sync: true,
    last_sync_at: daysAgo(1),
    created_at: daysAgo(14),
    updated_at: daysAgo(1),
  },
  {
    id: VECTOR_EXPORT_2_ID,
    display_name: 'Product Documentation Vectors',
    description: 'Technical documentation embeddings for support agent',
    dataset_id: 'gds00001',
    aws_account_id: 'acc-001',
    region: 'us-east-1',
    status: 'completed',
    vector_count: 3842,
    vector_bucket_name: 'aivilo-vectors-product-docs-prod',
    index_name: 'product-docs-index',
    embedding_model: 'amazon.titan-embed-text-v2:0',
    embedding_dimensions: 1024,
    auto_sync: false,
    last_sync_at: daysAgo(3),
    created_at: daysAgo(21),
    updated_at: daysAgo(3),
  },
  {
    id: VECTOR_EXPORT_3_ID,
    display_name: 'Training Data Vectors (Staging)',
    description: 'Training dataset vectors for evaluation pipeline',
    dataset_id: 'gds00001',
    aws_account_id: 'acc-002',
    region: 'us-west-2',
    status: 'in_progress',
    vector_count: 892,
    vector_bucket_name: 'aivilo-vectors-training-staging',
    index_name: 'training-index',
    embedding_model: 'amazon.titan-embed-text-v2:0',
    embedding_dimensions: 1024,
    auto_sync: false,
    last_sync_at: null,
    created_at: hoursAgo(2),
    updated_at: hoursAgo(2),
  }
];

// ============================================================================
// AI OPERATIONS MOCK DATA (for aiOperationsApi.js interception)
// ============================================================================

export const getAIOpsMockData = (endpoint: string): any => {
  const cleanEndpoint = endpoint.split('?')[0];
  const url = new URL(endpoint, 'http://demo.local');
  let selectedProjectId = url.searchParams.get('project_id');
  // Resolve workload_id to project_id for filtering
  const selectedWorkloadId = url.searchParams.get('workload_id');
  if (!selectedProjectId && selectedWorkloadId) {
    const workloadToProject: Record<string, string> = {
      [WORKLOAD_AI_PROD_ID]: PROJECT_AI_TRANSFORM_ID,
      [WORKLOAD_NON_PROD_ID]: PROJECT_CLOUD_MIGRATION_ID,
      [WORKLOAD_DATA_PIPELINE_ID]: PROJECT_DATA_LAKE_ID,
    };
    selectedProjectId = workloadToProject[selectedWorkloadId] || null;
  }
  // Resolve account_id to project_id via workload account scopes
  const selectedAccountId = url.searchParams.get('account_id');
  if (!selectedProjectId && selectedAccountId) {
    const accountToProjects: Record<string, string> = {
      '123456789012': PROJECT_AI_TRANSFORM_ID,
      '987654321098': PROJECT_CLOUD_MIGRATION_ID,
    };
    selectedProjectId = accountToProjects[selectedAccountId] || null;
  }
  // Resolve environment_id to project_id
  const selectedEnvironmentId = url.searchParams.get('environment_id');
  if (!selectedProjectId && selectedEnvironmentId) {
    const envToProjects: Record<string, string> = {
      'env-0000-0000-0000-000000000001': PROJECT_AI_TRANSFORM_ID,
      'env-0000-0000-0000-000000000002': PROJECT_AI_TRANSFORM_ID,
      'env-0000-0000-0000-000000000003': PROJECT_CLOUD_MIGRATION_ID,
      'env-0000-0000-0000-000000000004': PROJECT_CLOUD_MIGRATION_ID,
    };
    selectedProjectId = envToProjects[selectedEnvironmentId] || null;
  }
  const demoProjectId = PROJECT_AI_TRANSFORM_ID;
  const projectToWorkloadMap: Record<string, { id: string; name: string }> = {
    [PROJECT_AI_TRANSFORM_ID]: { id: WORKLOAD_AI_PROD_ID, name: 'Marketing AI Platform' },
    [PROJECT_CLOUD_MIGRATION_ID]: { id: WORKLOAD_NON_PROD_ID, name: 'Financial Data Pipeline' },
    [PROJECT_DATA_LAKE_ID]: { id: WORKLOAD_DATA_PIPELINE_ID, name: 'Legacy Application' },
  };
  const projectToAccountMap: Record<string, string> = {
    [PROJECT_AI_TRANSFORM_ID]: '123456789012',
    [PROJECT_CLOUD_MIGRATION_ID]: '987654321098',
    [PROJECT_DATA_LAKE_ID]: '123456789012',
  };
  const aiOpsEnvList = [
    'env-0000-0000-0000-000000000001',
    'env-0000-0000-0000-000000000002',
    'env-0000-0000-0000-000000000003',
    'env-0000-0000-0000-000000000004',
  ];
  let aiOpsItemCounter = 0;
  const withProject = <T extends Record<string, any>>(item: T): T & { project_id: string; workload_id?: string; workload_name?: string; account_id?: string; environment_id?: string } => {
    const pid = item.project_id || demoProjectId;
    const wl = projectToWorkloadMap[pid];
    const accountId = projectToAccountMap[pid];
    // Distribute across environments
    const envIdx = accountId === '123456789012'
      ? (aiOpsItemCounter % 2 === 0 ? 0 : 1)
      : (aiOpsItemCounter % 2 === 0 ? 2 : 3);
    aiOpsItemCounter++;
    return {
      ...item,
      project_id: pid,
      ...(wl ? { workload_id: wl.id, workload_name: wl.name } : {}),
      ...(accountId ? { account_id: accountId } : {}),
      environment_id: aiOpsEnvList[envIdx],
    };
  };
  const filterBySelectedProject = <T extends Record<string, any>>(items: T[]): (T & { project_id: string })[] => {
    const enriched = items.map(withProject);
    // Filter by the active dimension directly (not just project)
    if (selectedWorkloadId) return enriched.filter((item) => item.workload_id === selectedWorkloadId);
    if (selectedAccountId) return enriched.filter((item) => item.account_id === selectedAccountId);
    if (selectedEnvironmentId) return enriched.filter((item) => item.environment_id === selectedEnvironmentId);
    if (selectedProjectId) return enriched.filter((item) => item.project_id === selectedProjectId);
    return enriched;
  };
  const buildTraceMetrics = (traces: Array<Record<string, any>>) => {
    const totalTraces = traces.length;
    const totalCost = traces.reduce((sum, trace) => sum + (trace.cost || 0), 0);
    const totalTokens = traces.reduce((sum, trace) => sum + (trace.total_tokens || 0), 0);
    const totalLatency = traces.reduce((sum, trace) => sum + (trace.latency_ms || 0), 0);
    const errorCount = traces.filter((trace) => trace.outcome === 'error').length;
    const uniqueSessions = new Set(traces.map((trace) => trace.session_id).filter(Boolean)).size;
    return {
      totalTraces,
      totalCost,
      totalTokens,
      avgLatency: totalTraces > 0 ? totalLatency / totalTraces : 0,
      errorRate: totalTraces > 0 ? (errorCount / totalTraces) * 100 : 0,
      uniqueSessions,
    };
  };
  const projectTraces = filterBySelectedProject(mockGenAITraces);
  const projectTraceMetrics = buildTraceMetrics(projectTraces);
  const projectPrompts = filterBySelectedProject(mockPrompts);
  const projectEvaluations = filterBySelectedProject(mockEvaluations);

  // === GenAIOps Overview ===
  if (cleanEndpoint.includes('/genaiops/overview/metrics')) {
    return {
      total_traces: projectTraceMetrics.totalTraces,
      total_cost: Number.parseFloat(projectTraceMetrics.totalCost.toFixed(4)),
      avg_latency_ms: Number.parseFloat(projectTraceMetrics.avgLatency.toFixed(2)),
      p95_latency_ms: projectTraceMetrics.totalTraces > 0 ? Math.max(...projectTraces.map((trace) => trace.latency_ms || 0)) : 0,
      p99_latency_ms: projectTraceMetrics.totalTraces > 0 ? Math.max(...projectTraces.map((trace) => trace.latency_ms || 0)) : 0,
      error_rate: Number.parseFloat(projectTraceMetrics.errorRate.toFixed(2)),
      active_prompts: projectPrompts.length,
      active_agents: new Set(projectTraces.map((trace) => trace.agent_name).filter(Boolean)).size,
      total_tokens: projectTraceMetrics.totalTokens,
      quality_score: projectTraceMetrics.totalTraces > 0 ? 71 : 0,
      prompts_count: projectPrompts.length,
      prompts: { total: projectPrompts.length },
      unique_sessions: projectTraceMetrics.uniqueSessions,
      total_runs: projectEvaluations.length,
      agents: { unique_sessions: projectTraceMetrics.uniqueSessions, total_runs: projectEvaluations.length, total_tokens: projectTraceMetrics.totalTokens },
      traces_24h: projectTraces.filter((trace) => new Date(trace.created_at || trace.timestamp).getTime() >= Date.now() - (24 * 60 * 60 * 1000)).length,
      cost_24h: Number.parseFloat(projectTraces.filter((trace) => new Date(trace.created_at || trace.timestamp).getTime() >= Date.now() - (24 * 60 * 60 * 1000)).reduce((sum, trace) => sum + (trace.cost || 0), 0).toFixed(4)),
      latency_trend: 'up',
      cost_trend: 'up',
      error_trend: projectTraceMetrics.errorRate > 0 ? 'up' : 'flat',
      warnings: projectTraceMetrics.totalTraces > 0 ? [
        { type: 'evaluation_regression', message: 'product-recommendation score dropped from 0.92 to 0.71', severity: 'high' },
        { type: 'cost_spike', message: 'AI model costs up 48% vs last period', severity: 'medium' }
      ] : []
    };
  }

  // === Traces ===
  if (cleanEndpoint === '/genaiops/traces' || cleanEndpoint.endsWith('/genaiops/traces')) {
    const sortedTraces = filterBySelectedProject(mockGenAITraces).sort((a, b) =>
      new Date(b.created_at || b.timestamp).getTime() - new Date(a.created_at || a.timestamp).getTime()
    );
    return {
      data: sortedTraces,
      meta: { page: 1, limit: 50, total: sortedTraces.length, totalPages: 1 }
    };
  }

  if (cleanEndpoint.includes('/genaiops/traces/timeseries/hourly')) {
    const hourlyBuckets = Array.from({ length: 24 }, (_, i) => {
      const bucketStart = Date.now() - (23 - i) * 60 * 60 * 1000;
      const bucketEnd = bucketStart + 60 * 60 * 1000;
      const bucketTraces = projectTraces.filter((trace) => {
        const timestamp = new Date(trace.created_at || trace.timestamp).getTime();
        return timestamp >= bucketStart && timestamp < bucketEnd;
      });
      return {
        hour: new Date(bucketStart).toISOString(),
        period: `${23 - i}h ago`,
        count: bucketTraces.length,
        value: bucketTraces.length,
        total_cost: Number.parseFloat(bucketTraces.reduce((sum, trace) => sum + (trace.cost || 0), 0).toFixed(4)),
        error_count: bucketTraces.filter((trace) => trace.outcome === 'error').length
      };
    });
    return {
      data: hourlyBuckets
    };
  }

  // Single trace
  const traceMatch = cleanEndpoint.match(/\/genaiops\/traces\/([^/]+)$/);
  if (traceMatch) {
    const traceId = traceMatch[1];
    return withProject(mockGenAITraces.find(t => t.id === traceId) || mockGenAITraces[0]);
  }

  // === Sessions ===
  if (cleanEndpoint.includes('/genaiops/sessions') && !cleanEndpoint.includes('/messages')) {
    const allSessions = filterBySelectedProject([
      { id: SESSION_5_ID, session_id: SESSION_5_ID, user_id: 'user-789', agent_name: 'customer-support-agent', started_at: hoursAgo(2), ended_at: hoursAgo(1.5), traces_count: 1, message_count: 4, total_cost: 0.041, total_tokens: 4090, duration_ms: 1800000, status: 'error', project_id: PROJECT_AI_TRANSFORM_ID },
      { id: SESSION_2_ID, session_id: SESSION_2_ID, user_id: 'user-123', agent_name: 'product-recommendation', started_at: hoursAgo(8), ended_at: hoursAgo(3), traces_count: 3, message_count: 18, total_cost: 0.121, total_tokens: 11936, duration_ms: 18000000, status: 'degraded', project_id: PROJECT_CLOUD_MIGRATION_ID },
      { id: SESSION_4_ID, session_id: SESSION_4_ID, user_id: 'user-123', agent_name: 'product-recommendation', started_at: daysAgo(3), ended_at: daysAgo(3), traces_count: 1, message_count: 6, total_cost: 0.013, total_tokens: 1801, duration_ms: 1240, status: 'completed', project_id: PROJECT_AI_TRANSFORM_ID },
      { id: SESSION_3_ID, session_id: SESSION_3_ID, user_id: 'user-456', agent_name: 'customer-support-agent', started_at: daysAgo(6), ended_at: daysAgo(5), traces_count: 2, message_count: 14, total_cost: 0.025, total_tokens: 3500, duration_ms: 86400000, status: 'completed', project_id: PROJECT_CLOUD_MIGRATION_ID },
      { id: SESSION_1_ID, session_id: SESSION_1_ID, user_id: 'user-123', agent_name: 'product-recommendation', started_at: daysAgo(7), ended_at: daysAgo(7), traces_count: 1, message_count: 8, total_cost: 0.012, total_tokens: 1700, duration_ms: 1120, status: 'completed', project_id: PROJECT_AI_TRANSFORM_ID }
    ]);

    const sessionMatch = cleanEndpoint.match(/\/genaiops\/sessions\/([^/]+)$/);
    if (sessionMatch) {
      const sid = sessionMatch[1];
      const found = allSessions.find(s => s.id === sid) || allSessions[0];
      // Build mock messages from traces belonging to this session
      const sessionTraces = mockGenAITraces.filter(t => t.session_id === sid);
      const messages = sessionTraces.flatMap((t, i) => [
        { id: `msg-${sid}-${i}-user`, role: 'user', content: t.prompt || 'User input', timestamp: t.created_at, tokens: t.input_tokens },
        { id: `msg-${sid}-${i}-assistant`, role: 'assistant', content: t.completion || 'Assistant response', timestamp: t.created_at, tokens: t.output_tokens, cost: t.cost }
      ]);
      return { session: found, messages };
    }
    return {
      data: allSessions,
      meta: { page: 1, limit: 50, total: allSessions.length, totalPages: 1 }
    };
  }

  if (cleanEndpoint.includes('/messages')) {
    return { data: [] };
  }

  // === Prompts ===
  if (cleanEndpoint === '/genaiops/prompts' || cleanEndpoint.endsWith('/genaiops/prompts')) {
    return {
      data: filterBySelectedProject(mockPrompts),
      meta: { page: 1, limit: 50, total: filterBySelectedProject(mockPrompts).length, totalPages: 1 }
    };
  }

  // Prompt analytics
  const promptAnalyticsMatch = cleanEndpoint.match(/\/genaiops\/prompts\/([^/]+)\/analytics/);
  if (promptAnalyticsMatch) {
    const promptId = promptAnalyticsMatch[1];
    const prompt = mockPrompts.find(p => [p.id, p.name].includes(promptId));
    const usageCount = prompt?.usage_count || 0;
    const avgCost = 0.0075;
    const totalCost = usageCount * avgCost;
    return {
      total_usage: usageCount,
      invocations: usageCount,
      cost_analysis: {
        total_cost: Number.parseFloat(totalCost.toFixed(2)),
        average_cost_per_invocation: avgCost
      },
      token_usage: {
        total_input: usageCount * 1400,
        total_output: usageCount * 600,
        total: usageCount * 2000
      }
    };
  }

  // Single prompt or prompt versions
  const promptVersionMatch = cleanEndpoint.match(/\/genaiops\/prompts\/([^/]+)\/versions/);
  if (promptVersionMatch) {
    const promptId = promptVersionMatch[1];
    const prompt = mockPrompts.find(p => [p.id, p.name].includes(promptId));
    // RollbackModal expects { versions: [...] } with version_number field
    const versions = (prompt?.versions || []).map(v => ({
      ...v,
      id: `${prompt?.id || promptId}-v${v.version}`,
      version_number: v.version,
      description: v.status === 'archived' ? 'Previous production version' : 'Current active version',
      created_at: v.created_at
    }));
    return { versions };
  }

  const promptMatch = cleanEndpoint.match(/\/genaiops\/prompts\/([^/]+)$/);
  if (promptMatch) {
    const promptId = promptMatch[1];
    return withProject(mockPrompts.find(p => [p.id, p.name].includes(promptId)) || mockPrompts[0]);
  }

  // === Evaluations ===
  if (cleanEndpoint.includes('/genaiops/evaluations/metrics')) {
    return {
      summary: {
        total_evaluations: projectEvaluations.length,
        avg_score: 81.5,
        pass_rate: 75,
        avg_duration: 4500,
        total_cost: 0.494
      },
      recent_evaluations: projectEvaluations.map(e => ({
        id: e.id,
        model: e.model || 'claude-sonnet-4-5',
        score: e.avg_score ? e.avg_score * 100 : 71,
        status: e.status || 'completed',
        duration: e.duration || 4500,
        timestamp: e.timestamp || e.run_date || new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
        dataset: e.dataset || 'customer-interactions-v3'
      }))
    };
  }

  if (cleanEndpoint === '/genaiops/evaluations' || cleanEndpoint.includes('/genaiops/evaluations')) {
    return {
      data: projectEvaluations,
      total: projectEvaluations.length,
      meta: { page: 1, limit: 50, total: projectEvaluations.length, totalPages: 1 }
    };
  }

  // Single evaluation
  const evalMatch = cleanEndpoint.match(/\/genaiops\/evaluations\/([^/]+)$/);
  if (evalMatch) {
    return withProject(mockEvaluations.find(e => e.id === evalMatch[1]) || mockEvaluations[0]);
  }

  // Evaluation results
  const evalResultsMatch = cleanEndpoint.match(/\/genaiops\/evaluations\/([^/]+)\/results/);
  if (evalResultsMatch) {
    return { data: [], meta: { total: 0 } };
  }

  // === Datasets (GenAIOps datasets - different from DataOps) ===
  if (cleanEndpoint.includes('/genaiops/datasets')) {
    // Handle dataset vector exports sub-resource first
    if (cleanEndpoint.includes('/vector-exports')) {
      const dsVeMatch = cleanEndpoint.match(/\/genaiops\/datasets\/([^/]+)\/vector-exports/);
      if (dsVeMatch) {
        const datasetId = dsVeMatch[1];
        const filtered = mockVectorExports().filter(e => e.dataset_id === datasetId);
        return { data: filtered, meta: { page: 1, limit: 50, total: filtered.length, totalPages: 1 } };
      }
      return { data: [], meta: { page: 1, limit: 50, total: 0, totalPages: 1 } };
    }
    const dsMatch = cleanEndpoint.match(/\/genaiops\/datasets\/([^/]+)/);
    if (dsMatch) {
      if (cleanEndpoint.includes('/variables')) {
        return { variables: ['interaction_context', 'customer_id', 'timestamp'], sample_values: {} };
      }
      return withProject({ id: dsMatch[1], name: 'customer-classifier-test-set', test_cases_count: 100, is_active: true });
    }
    const projectDatasets = filterBySelectedProject([
      { id: 'gds00001', name: 'customer-classifier-test-set', test_cases_count: 100, is_active: true, created_at: daysAgo(30), project_id: PROJECT_AI_TRANSFORM_ID },
      { id: 'gds00002', name: 'migration-cutover-suite', test_cases_count: 64, is_active: true, created_at: daysAgo(14), project_id: PROJECT_CLOUD_MIGRATION_ID },
      { id: 'gds00003', name: 'lakehouse-quality-benchmark', test_cases_count: 48, is_active: true, created_at: daysAgo(7), project_id: PROJECT_DATA_LAKE_ID }
    ]);
    return {
      data: projectDatasets,
      meta: { page: 1, limit: 50, total: projectDatasets.length, totalPages: 1 }
    };
  }

  // === Credentials ===
  if (cleanEndpoint.includes('/genaiops/credentials')) {
    if (cleanEndpoint.includes('/available-models')) {
      return { models: [{ id: 'claude-sonnet-4-5', provider: 'anthropic' }, { id: 'gpt-4o', provider: 'openai' }] };
    }
    if (cleanEndpoint.includes('/test')) {
      return { success: true, message: 'Connection successful' };
    }
    return [];
  }

  // === Judge Rubrics ===
  if (cleanEndpoint.includes('/genaiops/judge-rubrics')) {
    return { data: [], meta: { page: 1, limit: 50, total: 0, totalPages: 1 } };
  }

  // === Assertions ===
  if (cleanEndpoint.includes('/genaiops/assertions')) {
    return { data: [], meta: { page: 1, limit: 50, total: 0, totalPages: 1 } };
  }

  // === Promotion Policies ===
  if (cleanEndpoint === '/genaiops/environments' || cleanEndpoint.includes('/genaiops/environments')) {
    const environments = [
      { id: 'genv-development', name: 'development', display_name: 'Development', description: 'Fast iteration environment for prompt changes', color: '#3B82F6', is_terminal: false, position: 0 },
      { id: 'genv-staging', name: 'staging', display_name: 'Staging', description: 'Pre-production validation environment', color: '#F59E0B', is_terminal: false, position: 10 },
      { id: 'genv-production', name: 'production', display_name: 'Production', description: 'Customer-facing production environment', color: '#10B981', is_terminal: true, position: 20 }
    ];
    return {
      environments,
      promotion_chain: environments.map((environment) => environment.name)
    };
  }

  // === Promotion Policies ===
  if (cleanEndpoint.includes('/genaiops/promotion-policies')) {
    return {
      data: [
        {
          id: 'pol000001',
          name: 'Production Quality Gate',
          is_active: true,
          trigger: 'always',
          prompt_name: '*',
          dataset_id: 'gds00001',
          description: 'All prompts must pass evaluation before production deployment',
          quality_gate_config: {
            min_pass_rate: 0.85,
            min_avg_score: 0.8,
            max_avg_latency_ms: 5000,
            max_total_cost: 0.5
          },
          evaluation_config: {
            provider: 'anthropic',
            model_id: 'claude-sonnet-4-5',
            scoring_method: 'llm_judge',
            pass_threshold: 0.8
          }
        },
        {
          id: 'pol000002',
          name: 'Staging Smoke Test',
          is_active: true,
          trigger: 'staging_only',
          prompt_name: 'product-recommendation',
          dataset_id: 'gds00001',
          description: 'Quick validation before staging deployment',
          quality_gate_config: {
            min_pass_rate: 0.7,
            min_avg_score: 0.65,
            max_avg_latency_ms: null,
            max_total_cost: null
          },
          evaluation_config: {
            provider: 'anthropic',
            model_id: 'claude-sonnet-4-5',
            scoring_method: 'contains',
            pass_threshold: 0.7
          }
        }
      ],
      meta: { page: 1, limit: 50, total: 2, totalPages: 1 }
    };
  }

  // === Approvals ===
  if (cleanEndpoint.includes('/genaiops/approvals')) {
    const allApprovals = filterBySelectedProject([
      {
        id: 'apr000001',
        resource_type: 'prompt',
        resource_name: 'product-recommendation',
        resource_id: PROMPT_CUSTOMER_CLASSIFIER_ID,
        action: 'promote',
        from_environment: 'staging',
        to_environment: 'production',
        status: 'approved',
        requested_by: 'sarah.chen@techcorp.com',
        requested_at: daysAgo(15),
        approved_by: 'marcus.johnson@techcorp.com',
        approved_at: daysAgo(14),
        reason: 'Improved evaluation score from 0.88 to 0.92. Ready for production.',
        project_id: PROJECT_AI_TRANSFORM_ID,
        metadata: { version: 2, current_version: 1 }
      },
      {
        id: 'apr000002',
        resource_type: 'prompt',
        resource_name: 'cloud-cost-triage',
        resource_id: PROMPT_CODE_REVIEW_ID,
        action: 'promote',
        from_environment: 'development',
        to_environment: 'staging',
        status: 'pending',
        requested_by: 'james.kim@techcorp.com',
        requested_at: hoursAgo(9),
        reason: 'Ready for staging validation after migration cutover fixes.',
        project_id: PROJECT_CLOUD_MIGRATION_ID,
        metadata: { version: 3, current_version: 2 }
      },
      {
        id: 'apr000003',
        resource_type: 'dataset',
        resource_name: 'lakehouse-quality-benchmark',
        resource_id: 'gds00003',
        action: 'promote',
        from_environment: 'staging',
        to_environment: 'production',
        status: 'pending',
        requested_by: 'emily.watson@techcorp.com',
        requested_at: hoursAgo(4),
        reason: 'Dataset benchmark suite is ready for production governance checks.',
        project_id: PROJECT_DATA_LAKE_ID,
        metadata: { version: 1, current_version: 1 }
      }
    ]);
    if (cleanEndpoint.includes('/summary')) {
      const pendingApprovals = allApprovals.filter((approval) => approval.status === 'pending');
      return {
        total_pending: pendingApprovals.length,
        approved: allApprovals.filter((approval) => approval.status === 'approved').length,
        rejected: allApprovals.filter((approval) => approval.status === 'rejected').length,
        expiring_soon: 1,
        oldest_pending_days: pendingApprovals.length > 0 ? 1 : 0,
        pending_by_type: {
          prompt_release: pendingApprovals.filter((approval) => approval.resource_type === 'prompt').length,
          model_deployment: pendingApprovals.filter((approval) => approval.resource_type === 'dataset').length
        }
      };
    }
    if (cleanEndpoint.includes('/releases/history')) {
      // ReleasesContent expects a plain array
      return [
        {
          id: 'rel000001',
          prompt_name: 'product-recommendation',
          prompt_id: PROMPT_CUSTOMER_CLASSIFIER_ID,
          version_number: 2,
          environment: 'staging',
          release_type: 'approved',
          pass_rate: 0.71,
          avg_score: 0.68,
          released_by: 'sarah.chen@techcorp.com',
          released_at: daysAgo(3)
        },
        {
          id: 'rel000002',
          prompt_name: 'product-recommendation',
          prompt_id: PROMPT_CUSTOMER_CLASSIFIER_ID,
          version_number: 1,
          environment: 'production',
          release_type: 'approved',
          pass_rate: 0.92,
          avg_score: 0.89,
          released_by: 'marcus.johnson@techcorp.com',
          released_at: daysAgo(14)
        },
        {
          id: 'rel000003',
          prompt_name: 'product-recommendation',
          prompt_id: PROMPT_CUSTOMER_CLASSIFIER_ID,
          version_number: 1,
          environment: 'staging',
          release_type: 'direct',
          pass_rate: 0.92,
          avg_score: 0.89,
          released_by: 'sarah.chen@techcorp.com',
          released_at: daysAgo(21)
        }
      ];
    }
    if (cleanEndpoint.includes('/audit/report')) {
      // AuditLogContent expects { logs: [...], total: number }
      return {
        logs: [
          {
            id: 'aud000001',
            timestamp: hoursAgo(2),
            action: 'promote',
            resource_type: 'prompt',
            resource_name: 'product-recommendation',
            resource_id: PROMPT_CUSTOMER_CLASSIFIER_ID,
            user_id: 'sarah.chen@techcorp.com',
            environment: 'staging',
            version_number: 2,
            pass_rate: 0.71,
            details: { reason: 'Regression evaluation - score dropped from 0.92 to 0.71' }
          },
          {
            id: 'aud000002',
            timestamp: daysAgo(1),
            action: 'approve',
            resource_type: 'prompt',
            resource_name: 'product-recommendation',
            resource_id: PROMPT_CUSTOMER_CLASSIFIER_ID,
            user_id: 'marcus.johnson@techcorp.com',
            environment: 'production',
            version_number: 1,
            pass_rate: 0.92,
            details: { approval_id: 'apr000001', notes: 'Quality gate passed - baseline version' }
          },
          {
            id: 'aud000003',
            timestamp: daysAgo(3),
            action: 'create',
            resource_type: 'prompt',
            resource_name: 'product-recommendation',
            resource_id: PROMPT_CUSTOMER_CLASSIFIER_ID,
            user_id: 'sarah.chen@techcorp.com',
            details: { version: 2, change_summary: 'Updated prompt template with new context window' }
          },
          {
            id: 'aud000004',
            timestamp: daysAgo(5),
            action: 'update',
            resource_type: 'dataset',
            resource_name: 'customer-classifier-test-set',
            resource_id: 'gds00001',
            user_id: 'emily.wong@techcorp.com',
            details: { test_cases_added: 15, total_test_cases: 100 }
          },
          {
            id: 'aud000005',
            timestamp: daysAgo(14),
            action: 'promote',
            resource_type: 'prompt',
            resource_name: 'product-recommendation',
            resource_id: PROMPT_CUSTOMER_CLASSIFIER_ID,
            user_id: 'marcus.johnson@techcorp.com',
            environment: 'production',
            version_number: 1,
            pass_rate: 0.92,
            details: { approval_id: 'apr000002', notes: 'Initial production deployment' }
          }
        ],
        total: 5
      };
    }
    const requestedStatus = url.searchParams.get('status');
    const requestedResourceType = url.searchParams.get('resource_type');
    return allApprovals.filter((approval) => {
      if (requestedStatus && approval.status !== requestedStatus) return false;
      if (requestedResourceType && approval.resource_type !== requestedResourceType) return false;
      return true;
    });
  }

  // === Analytics ===
  if (cleanEndpoint.includes('/genaiops/analytics/anomalies')) {
    // CostsContent expects a plain array with metric_name, current_value, expected_value, deviation_std
    return [
      {
        id: ANOMALY_ID,
        metric_name: 'cost',
        severity: 'high',
        detected_at: hoursAgo(6),
        description: 'AI model costs spiked 62% in Production environment, correlated with SageMaker and Bedrock usage increase',
        environment: 'Production',
        expected_value: 7334,
        current_value: 11890.4,
        deviation_std: 2.8
      }
    ];
  }

  if (cleanEndpoint.includes('/genaiops/analytics/cost-forecast')) {
    // CostsContent expects: total_forecast, confidence_low, confidence_high, trend, forecast_daily (array with date + cost), recommendations
    return {
      total_forecast: 3850,
      confidence_low: 3460,
      confidence_high: 4240,
      trend: 'increasing',
      forecast_daily: Array.from({ length: 30 }, (_, i) => ({
        date: new Date(Date.now() + i * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
        cost: 116 + i * 0.8
      })),
      recommendations: [
        'Consider reserved capacity for SageMaker endpoints to reduce costs by ~25%',
        'Review Bedrock model selection - switching to smaller models for simple tasks could save ~$55/month'
      ]
    };
  }

  if (cleanEndpoint.includes('/genaiops/analytics/cost-breakdown')) {
    const costByAgent = projectTraces.reduce<Record<string, { total_cost: number; traces: number }>>((acc, trace) => {
      const key = trace.agent_name || trace.name || 'Unknown';
      if (!acc[key]) {
        acc[key] = { total_cost: 0, traces: 0 };
      }
      acc[key].total_cost += trace.cost || 0;
      acc[key].traces += 1;
      return acc;
    }, {});
    const breakdown = Object.entries(costByAgent).map(([name, metrics]) => ({
      name,
      dimension: name,
      total_cost: Number.parseFloat(metrics.total_cost.toFixed(4)),
      cost: Number.parseFloat(metrics.total_cost.toFixed(4)),
      percentage: projectTraceMetrics.totalCost > 0 ? Number.parseFloat(((metrics.total_cost / projectTraceMetrics.totalCost) * 100).toFixed(1)) : 0,
      run_count: metrics.traces,
      traces: metrics.traces
    }));
    return {
      breakdown,
      data: breakdown,
      total_cost: Number.parseFloat(projectTraceMetrics.totalCost.toFixed(4)),
      total: Number.parseFloat(projectTraceMetrics.totalCost.toFixed(4))
    };
  }

  if (cleanEndpoint.includes('/genaiops/analytics/realtime')) {
    return {
      active_traces: projectTraceMetrics.totalTraces,
      traces_per_minute: Number.parseFloat((projectTraceMetrics.totalTraces / 60).toFixed(2)),
      avg_latency_ms: Number.parseFloat(projectTraceMetrics.avgLatency.toFixed(2)),
      p95_latency_ms: projectTraceMetrics.totalTraces > 0 ? Math.max(...projectTraces.map((trace) => trace.latency_ms || 0)) : 0,
      p99_latency_ms: projectTraceMetrics.totalTraces > 0 ? Math.max(...projectTraces.map((trace) => trace.latency_ms || 0)) : 0,
      error_rate: Number.parseFloat(projectTraceMetrics.errorRate.toFixed(2)),
      active_agents: new Set(projectTraces.map((trace) => trace.agent_name).filter(Boolean)).size,
      cost_per_hour: Number.parseFloat((projectTraceMetrics.totalCost / 24).toFixed(4)),
      quality_score: projectTraceMetrics.totalTraces > 0 ? 71 : 0,
      total_cost: Number.parseFloat(projectTraceMetrics.totalCost.toFixed(4)),
      total_tokens: projectTraceMetrics.totalTokens,
    };
  }

  if (cleanEndpoint.includes('/genaiops/analytics/compare-versions')) {
    return { version_a: { score: 0.92 }, version_b: { score: 0.71 }, delta: -0.21 };
  }

  // === Vector Exports ===
  if (cleanEndpoint.includes('/genaiops/vector-exports')) {
    const singleMatch = cleanEndpoint.match(/\/genaiops\/vector-exports\/([^/]+)$/);
    if (singleMatch) {
      const exportId = singleMatch[1];
      const allExports = mockVectorExports();
      return allExports.find(e => e.id === exportId) || allExports[0];
    }
    // Sync endpoint
    if (cleanEndpoint.includes('/sync')) {
      return { success: true, message: 'Sync started' };
    }
    // Usage example endpoint
    if (cleanEndpoint.includes('/usage-example')) {
      return {
        python: 'import boto3\nclient = boto3.client("s3vectors")\n# Query vectors...',
        cli: 'aws s3vectors query-vectors --vector-bucket-name my-bucket --index-name my-index',
        bedrock_kb: 'Use the S3 Vectors bucket ARN as a data source in your Bedrock Knowledge Base.'
      };
    }
    return {
      data: mockVectorExports(),
      meta: { page: 1, limit: 50, total: mockVectorExports().length, totalPages: 1 }
    };
  }

  // === Bedrock ===
  if (cleanEndpoint.includes('/genaiops/bedrock')) {
    if (cleanEndpoint.includes('/sync/status')) return { enabled: false };
    if (cleanEndpoint.includes('/quota')) return { status: 'healthy', quotas: [] };
    if (cleanEndpoint.includes('/mappings')) return { data: [] };
    if (cleanEndpoint.includes('/external-changes')) return { data: [] };
    if (cleanEndpoint.includes('/prompts')) return { data: [] };
    return {};
  }

  // === AgentOps Runs ===
  // Generate bulk background runs to populate the timeline chart
  const generateBulkRuns = () => {
    const bulkRuns = [];
    const agents = [
      { name: 'customer-support-agent', tools: ['classify_interaction', 'check_sla', 'route_ticket'], avgDuration: 35, avgCost: 0.0045 },
      { name: 'report-generator-agent', tools: ['data_analyzer', 'chart_generator', 'pdf_creator'], avgDuration: 150, avgCost: 0.008 }
    ];
    // Generate runs for days 7 through 1 (skip today — today's runs come from named mockAgentRuns)
    for (let day = 7; day >= 1; day--) {
      // customer-support-agent: 6-10 runs per day
      const supportCount = 6 + Math.floor((day * 3) % 5);
      for (let j = 0; j < supportCount; j++) {
        const a = agents[0];
        const hourOffset = 8 + j * (10 / supportCount); // spread across business hours
        const startTime = new Date(Date.now() - day * 86400000 + hourOffset * 3600000);
        bulkRuns.push({
          id: `ar-bulk-${day}-s-${j}`,
          agent_name: a.name,
          agent_id: AGENT_ESCALATION_ID,
          start_time: startTime.toISOString(),
          end_time: new Date(startTime.getTime() + a.avgDuration * 1000).toISOString(),
          duration_seconds: a.avgDuration + Math.round((j % 3 - 1) * 8),
          status: 'completed',
          outcome: 'success',
          steps_executed: 4 + (j % 2),
          tools_used: a.tools,
          cost: a.avgCost * (0.9 + (j % 3) * 0.1),
          trigger: 'automated',
          project_id: day % 2 === 0 ? PROJECT_CLOUD_MIGRATION_ID : PROJECT_AI_TRANSFORM_ID,
          metadata_json: JSON.stringify({ prompt_id: PROMPT_CUSTOMER_CLASSIFIER_ID, trigger: 'automated' })
        });
      }
      // report-generator-agent: 1-2 runs per day
      const reportCount = day % 3 === 0 ? 2 : 1;
      for (let j = 0; j < reportCount; j++) {
        const a = agents[1];
        const startTime = new Date(Date.now() - day * 86400000 + (6 + j * 4) * 3600000);
        bulkRuns.push({
          id: `ar-bulk-${day}-r-${j}`,
          agent_name: a.name,
          start_time: startTime.toISOString(),
          end_time: new Date(startTime.getTime() + a.avgDuration * 1000).toISOString(),
          duration_seconds: a.avgDuration + Math.round((j % 2 - 0.5) * 20),
          status: 'completed',
          outcome: 'success',
          steps_executed: 7 + (j % 2),
          tools_used: a.tools,
          cost: a.avgCost * (0.95 + j * 0.1),
          trigger: 'scheduled',
          project_id: PROJECT_DATA_LAKE_ID,
          metadata_json: JSON.stringify({ trigger: 'scheduled', report_type: 'daily_summary' })
        });
      }
    }
    return bulkRuns;
  };
  const projectRuns = filterBySelectedProject([...generateBulkRuns(), ...mockAgentRuns]);
  const projectViolations = filterBySelectedProject(mockSafetyViolations);
  const projectIncidents = filterBySelectedProject(mockIncidents);

  if (cleanEndpoint === '/agentops/runs' || cleanEndpoint.endsWith('/agentops/runs')) {
    const allRuns = projectRuns.sort((a, b) =>
      new Date((b as any).start_time || (b as any).created_at).getTime() - new Date((a as any).start_time || (a as any).created_at).getTime()
    );
    return {
      data: allRuns,
      meta: { page: 1, limit: 500, total: allRuns.length, totalPages: 1 }
    };
  }

  // Single run
  const runMatch = cleanEndpoint.match(/\/agentops\/runs\/([^/]+)$/);
  if (runMatch) {
    const allRuns = [...generateBulkRuns(), ...mockAgentRuns];
    return withProject(allRuns.find(r => r.id === runMatch[1]) || mockAgentRuns[0]);
  }

  // Run steps
  const stepsMatch = cleanEndpoint.match(/\/agentops\/runs\/([^/]+)\/steps/);
  if (stepsMatch) {
    const runId = stepsMatch[1];
    if (runId === AGENT_RUN_BAD_ID) {
      return {
        steps: [
          { id: 'step-b01', step_number: 1, tool_used: 'classify_interaction', action: 'Classify customer interaction', status: 'completed', duration_seconds: 4.2, input: 'Customer message: "This is unacceptable service"', output: 'Classification: unknown, Confidence: 0.41', input_tokens: 850, output_tokens: 120, cost: 0.0082 },
          { id: 'step-b02', step_number: 2, tool_used: 'check_sla', action: 'Check SLA compliance', status: 'completed', duration_seconds: 0.12, input: 'Ticket ID: TK-4821', output: 'SLA check passed', input_tokens: 200, output_tokens: 50, cost: 0.0008 },
          { id: 'step-b03', step_number: 3, tool_used: 'classify_interaction', action: 'Retry classification (low confidence)', status: 'completed', duration_seconds: 4.8, input: 'Retry with additional context', output: 'Retry: Classification: unknown, Confidence: 0.38', input_tokens: 1100, output_tokens: 130, cost: 0.0095 },
          { id: 'step-b04', step_number: 4, tool_used: 'classify_interaction', action: 'Retry classification attempt 2', status: 'completed', duration_seconds: 5.1, input: 'Retry 2 with expanded prompt', output: 'Retry 2: Classification: complaint, Confidence: 0.44', input_tokens: 1200, output_tokens: 140, cost: 0.0102 },
          { id: 'step-b05', step_number: 5, tool_used: 'route_ticket', action: 'Route ticket to queue', status: 'completed', duration_seconds: 0.34, input: 'Classification: complaint, Queue: general', output: 'Routed to general queue', input_tokens: 300, output_tokens: 60, cost: 0.0012 },
          { id: 'step-b06', step_number: 6, tool_used: 'override_priority', action: 'Override ticket priority', status: 'completed', duration_seconds: 0.89, input: 'Low confidence trigger: escalate to critical', output: 'Priority overridden to critical (low confidence trigger)', input_tokens: 400, output_tokens: 80, cost: 0.0025 },
          { id: 'step-b07', step_number: 7, tool_used: 'send_notification', action: 'Send escalation notification', status: 'completed', duration_seconds: 0.45, input: 'Notify escalation team of critical ticket', output: 'Notification sent to escalation team', input_tokens: 350, output_tokens: 70, cost: 0.0018 },
          { id: 'step-b08', step_number: 8, tool_used: 'external_api_call', action: 'Create CRM ticket', status: 'completed', duration_seconds: 2.34, input: 'Create ticket in CRM with critical priority', output: 'CRM ticket created with critical priority', input_tokens: 500, output_tokens: 100, cost: 0.0035 },
          { id: 'step-b09', step_number: 9, tool_used: 'send_notification', action: 'Send customer notification', status: 'completed', duration_seconds: 0.43, input: 'Notify customer of escalation', output: 'Customer notification sent', input_tokens: 300, output_tokens: 60, cost: 0.0015 },
          { id: 'step-b10', step_number: 10, tool_used: 'send_notification', action: 'Send manager notification', status: 'completed', duration_seconds: 0.46, input: 'Escalation alert to manager', output: 'Manager escalation notification sent', input_tokens: 320, output_tokens: 65, cost: 0.0016 },
          { id: 'step-b11', step_number: 11, tool_used: 'route_ticket', action: 'Re-route to VIP queue', status: 'completed', duration_seconds: 0.35, input: 'Re-route: VIP support queue', output: 'Re-routed to VIP support queue', input_tokens: 280, output_tokens: 55, cost: 0.0014 }
        ]
      };
    }
    return {
      steps: [
        { id: 'step-g01', step_number: 1, tool_used: 'classify_interaction', action: 'Classify customer interaction', status: 'completed', duration_seconds: 1.24, input: 'Customer message: "I need help with my bill"', output: 'Classification: billing_inquiry, Confidence: 0.94', input_tokens: 600, output_tokens: 90, cost: 0.0012 },
        { id: 'step-g02', step_number: 2, tool_used: 'check_sla', action: 'Check SLA compliance', status: 'completed', duration_seconds: 0.12, input: 'Ticket ID: TK-4820', output: 'SLA check passed', input_tokens: 200, output_tokens: 50, cost: 0.0005 },
        { id: 'step-g03', step_number: 3, tool_used: 'route_ticket', action: 'Route ticket to billing team', status: 'completed', duration_seconds: 0.34, input: 'Classification: billing_inquiry, Queue: billing', output: 'Routed to billing team', input_tokens: 300, output_tokens: 60, cost: 0.0008 },
        { id: 'step-g04', step_number: 4, tool_used: 'send_notification', action: 'Send confirmation notification', status: 'completed', duration_seconds: 0.45, input: 'Notify customer of ticket routing', output: 'Notification sent', input_tokens: 250, output_tokens: 50, cost: 0.0006 }
      ]
    };
  }

  // === Tool Usage ===
  if (cleanEndpoint.includes('/agentops/tools/usage')) {
    const projectToolRuns = projectRuns.filter((run) => Array.isArray(run.tools_used) && run.tools_used.length > 0);
    const toolCounts = projectToolRuns.reduce<Record<string, number>>((acc, run) => {
      run.tools_used.forEach((toolName: string) => {
        acc[toolName] = (acc[toolName] || 0) + 1;
      });
      return acc;
    }, {});
    const toolsWithDetails = mockAgentTools
      .filter((tool) => !selectedProjectId || (toolCounts[tool.name] || 0) > 0)
      .map(t => {
      const totalUses = selectedProjectId ? toolCounts[t.name] || 0 : t.usage_count;
      const failedUses = Math.round(totalUses * (1 - t.success_rate / 100));
      const successfulUses = totalUses - failedUses;
      const avgDurationSec = t.avg_execution_time_ms / 1000;
      return {
        ...t,
        tool_name: t.name,
        total_uses: totalUses,
        successful_uses: successfulUses,
        failed_uses: failedUses,
        success_rate: t.success_rate,
        avg_duration_seconds: avgDurationSec,
        latency_percentiles: {
          p50_seconds: avgDurationSec * 0.8,
          p95_seconds: avgDurationSec * 1.6,
          p99_seconds: avgDurationSec * 2.2
        },
        error_breakdown: failedUses > 0 ? {
          timeout: Math.round(failedUses * 0.6),
          rate_limit: Math.round(failedUses * 0.25),
          invalid_input: failedUses - Math.round(failedUses * 0.6) - Math.round(failedUses * 0.25)
        } : {},
        recent_errors: failedUses > 0 ? [
          { status: 'timeout', action: `${t.name} call timed out after ${(avgDurationSec * 3).toFixed(1)}s`, run_id: AGENT_RUN_BAD_ID, timestamp: hoursAgo(2) },
          { status: 'rate_limit', action: `Rate limit exceeded for ${t.name}`, run_id: AGENT_RUN_BAD_ID, timestamp: hoursAgo(5) }
        ] : [],
        timeline: Array.from({ length: 7 }, (_, i) => Math.round(totalUses / 7 * (0.85 + (i * 0.05))))
      };
    });

    const totalInvocations = toolsWithDetails.reduce((sum, t) => sum + t.total_uses, 0);
    const totalErrors = toolsWithDetails.reduce((sum, t) => sum + t.failed_uses, 0);

    return {
      tools: toolsWithDetails,
      summary: {
        unique_tools: toolsWithDetails.length,
        total_invocations: totalInvocations,
        total_errors: totalErrors,
        overall_success_rate: totalInvocations > 0 ? ((totalInvocations - totalErrors) / totalInvocations * 100) : 100,
        latency_percentiles: {
          p50_seconds: 0.34,
          p95_seconds: 2.1,
          p99_seconds: 3.8
        }
      },
      timeline: Array.from({ length: 7 }, (_, i) => ({
        date: new Date(Date.now() - (6 - i) * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
        total_calls: Math.round(totalInvocations / 7 * (0.85 + (i * 0.05))),
        unique_tools: toolsWithDetails.length
      }))
    };
  }

  // === Safety Violations ===
  if (cleanEndpoint.includes('/agentops/safety/violations/summary') || cleanEndpoint.includes('/agentops/safety/violations/stats')) {
    const bySeverity = projectViolations.reduce<Record<string, number>>((acc, violation) => {
      const key = violation.severity || 'low';
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, { critical: 0, high: 0, medium: 0, low: 0 });
    const byType = projectViolations.reduce<Record<string, number>>((acc, violation) => {
      const key = violation.violation_type || 'other';
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
    return {
      total: projectViolations.length,
      total_violations: projectViolations.length,
      violations_count: projectViolations.length,
      high_severity_count: bySeverity.high || 0,
      medium_severity_count: bySeverity.medium || 0,
      compliance_rate: projectRuns.length > 0 ? Number.parseFloat((((projectRuns.length - projectViolations.length) / projectRuns.length) * 100).toFixed(1)) : 100,
      by_severity: bySeverity,
      by_type: byType,
      trend: projectViolations.length > 0 ? 'up' : 'flat'
    };
  }

  if (cleanEndpoint.includes('/agentops/safety/violations')) {
    const violationMatch = cleanEndpoint.match(/\/agentops\/safety\/violations\/([^/]+)$/);
    if (violationMatch) {
      return withProject(mockSafetyViolations.find(v => v.id === violationMatch[1]) || mockSafetyViolations[0]);
    }
    return {
      data: projectViolations,
      meta: { page: 1, limit: 50, total: projectViolations.length, totalPages: 1 }
    };
  }

  // === Memory ===
  if (cleanEndpoint.includes('/agentops/memory')) {
    const runProjectMap = new Map([...generateBulkRuns(), ...mockAgentRuns].map((run) => [run.id, run.project_id || demoProjectId]));
    const now = new Date('2025-01-15T15:00:00Z');
    const nowMs = now.getTime();
    const memorySnapshots = [
      { id: 'ms-001', run_id: 'ar000000-0000-4000-a000-000000000002', memory_type: 'working', operation: 'write', token_count: 6800, max_tokens: 8192, timestamp: new Date(nowMs - 3600000).toISOString(), content_json: { context: 'Customer escalation decision', escalation_score: 0.91 } },
      { id: 'ms-002', run_id: 'ar000000-0000-4000-a000-000000000002', memory_type: 'conversation', operation: 'read', token_count: 4200, max_tokens: 8192, timestamp: new Date(nowMs - 3500000).toISOString(), content_json: { messages: 12, last_role: 'assistant' } },
      { id: 'ms-003', run_id: 'ar000000-0000-4000-a000-000000000001', memory_type: 'working', operation: 'write', token_count: 3100, max_tokens: 8192, timestamp: new Date(nowMs - 7200000).toISOString(), content_json: { context: 'Classification task', category: 'billing' } },
      { id: 'ms-004', run_id: 'ar000000-0000-4000-a000-000000000001', memory_type: 'tool_cache', operation: 'read', token_count: 1500, max_tokens: 8192, timestamp: new Date(nowMs - 7100000).toISOString(), content_json: { tool: 'customer_lookup', cached: true } },
      { id: 'ms-005', run_id: 'ar000000-0000-4000-a000-000000000002', memory_type: 'working', operation: 'prune', token_count: 7900, max_tokens: 8192, timestamp: new Date(nowMs - 2400000).toISOString(), content_json: { reason: 'Context limit approaching', pruned_tokens: 1200 } },
      { id: 'ms-006', run_id: 'ar000000-0000-4000-a000-000000000003', memory_type: 'conversation', operation: 'write', token_count: 2800, max_tokens: 8192, timestamp: new Date(nowMs - 10800000).toISOString(), content_json: { messages: 6, last_role: 'user' } },
      { id: 'ms-007', run_id: 'ar000000-0000-4000-a000-000000000002', memory_type: 'tool_cache', operation: 'update', token_count: 2100, max_tokens: 8192, timestamp: new Date(nowMs - 2000000).toISOString(), content_json: { tool: 'escalation_rules', version: 2 } },
      { id: 'ms-008', run_id: 'ar000000-0000-4000-a000-000000000003', memory_type: 'working', operation: 'read', token_count: 1800, max_tokens: 8192, timestamp: new Date(nowMs - 14400000).toISOString(), content_json: { context: 'Routine check' } }
    ].map((snapshot) => ({
      ...snapshot,
      project_id: runProjectMap.get(snapshot.run_id) || demoProjectId,
    }));
    const filteredSnapshots = filterBySelectedProject(memorySnapshots);
    if (cleanEndpoint.includes('/stats')) {
      return {
        total_snapshots: filteredSnapshots.length,
        avg_tokens: filteredSnapshots.length > 0 ? Math.round(filteredSnapshots.reduce((sum, snapshot) => sum + snapshot.token_count, 0) / filteredSnapshots.length) : 0,
        cache_hit_rate: filteredSnapshots.length > 0 ? 72.5 : 0,
        by_memory_type: filteredSnapshots.reduce<Record<string, number>>((acc, snapshot) => {
          acc[snapshot.memory_type] = (acc[snapshot.memory_type] || 0) + 1;
          return acc;
        }, {}),
        by_operation: filteredSnapshots.reduce<Record<string, number>>((acc, snapshot) => {
          acc[snapshot.operation] = (acc[snapshot.operation] || 0) + 1;
          return acc;
        }, {})
      };
    }
    const snapshotMatch = cleanEndpoint.match(/\/agentops\/memory\/([^/?]+)$/);
    if (snapshotMatch) {
      return filteredSnapshots.find((snapshot) => snapshot.id === snapshotMatch[1]) || filteredSnapshots[0] || null;
    }
    return { data: filteredSnapshots, meta: { page: 1, limit: 50, total: filteredSnapshots.length, totalPages: 1 } };
  }

  // === Incidents ===
  if (cleanEndpoint.includes('/agentops/incidents/stats')) {
    const bySeverity = projectIncidents.reduce<Record<string, number>>((acc, incident) => {
      const key = incident.severity || 'low';
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, { critical: 0, high: 0, medium: 0, low: 0 });
    const byStatus = projectIncidents.reduce<Record<string, number>>((acc, incident) => {
      const key = incident.status || 'active';
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
    return {
      total: projectIncidents.length,
      total_incidents: projectIncidents.length,
      incidents: projectIncidents,
      by_severity: bySeverity,
      by_status: byStatus,
      trend: projectIncidents.length > 0 ? 'up' : 'flat'
    };
  }

  if (cleanEndpoint.includes('/agentops/incidents')) {
    const incidentMatch = cleanEndpoint.match(/\/agentops\/incidents\/([^/]+)$/);
    if (incidentMatch) {
      return withProject(mockIncidents.find(i => i.id === incidentMatch[1]) || mockIncidents[0]);
    }
    return {
      data: projectIncidents,
      meta: { page: 1, limit: 50, total: projectIncidents.length, totalPages: 1 }
    };
  }

  // === APM ===
  if (cleanEndpoint.includes('/agentops/apm')) {
    // Parse query parameters from the full endpoint for metric-specific responses
    const apmQueryString = endpoint.includes('?') ? endpoint.split('?')[1] : '';
    const apmParams = new URLSearchParams(apmQueryString);
    const selectedAgent = apmParams.get('agent_name');
    const agentProfile = getDemoAPMAgentProfile(selectedAgent);
    const activeAgents = selectedAgent ? [selectedAgent] : [...DEMO_APM_AGENTS];
    const allProfiles = activeAgents.map((agent) => getDemoAPMAgentProfile(agent));

    if (cleanEndpoint.includes('/health/memory-leak-detection')) {
      return {
        agent_name: selectedAgent || 'customer-support-agent',
        window_hours: Number.parseInt(apmParams.get('window_hours') || '24'),
        leak_score: agentProfile.leakScore,
        confidence: agentProfile.leakConfidence,
        growth_rate_bytes_per_hour: agentProfile.leakGrowthRate,
        data_points: 288,
        trendline: {
          slope: Math.round(agentProfile.leakGrowthRate / 3600),
          r_squared: agentProfile.leakConfidence === 'high' ? 0.78 : agentProfile.leakConfidence === 'medium' ? 0.56 : 0.34,
        },
      };
    }
    if (cleanEndpoint.includes('/health')) {
      return {
        status: agentProfile.healthStatus,
        score: agentProfile.healthScore,
        health_score: agentProfile.healthScore,
        heap_used_percent: Math.round(agentProfile.heapPercent),
        heap_usage_percent: Math.round(agentProfile.heapPercent),
        cpu_percent: Math.round(agentProfile.cpuPercent),
        cpu_usage_percent: Math.round(agentProfile.cpuPercent),
        checks: [
          { name: 'cpu_usage', status: agentProfile.cpuPercent >= 85 ? 'critical' : agentProfile.cpuPercent >= 70 ? 'warning' : 'healthy', value: Number(agentProfile.cpuPercent.toFixed(1)), threshold: 80 },
          { name: 'memory_usage', status: agentProfile.heapPercent >= 80 ? 'critical' : agentProfile.heapPercent >= 70 ? 'warning' : 'healthy', value: Number(agentProfile.heapPercent.toFixed(1)), threshold: 70 },
          { name: 'error_rate', status: 'healthy', value: 0.8, threshold: 5 }
        ]
      };
    }
    if (cleanEndpoint.includes('/metrics/summary')) {
      if (!selectedAgent) {
        const avg = (values: number[]) => values.reduce((sum, value) => sum + value, 0) / values.length;
        const max = (values: number[]) => Math.max(...values);
        return {
          memory: {
            current_heap_percent: Number(avg(allProfiles.map((profile) => profile.heapPercent)).toFixed(1)),
            current_heap_used: Math.round(avg(allProfiles.map((profile) => profile.heapUsed))),
            current_heap_total: 536870912,
            current_heap_max: 1073741824,
            current_rss: Math.round(avg(allProfiles.map((profile) => profile.rss))),
            avg_heap_percent: Number(avg(allProfiles.map((profile) => profile.heapPercent - 4.2)).toFixed(1)),
            max_heap_percent: Number(max(allProfiles.map((profile) => profile.heapPercent)).toFixed(1)),
          },
          cpu: {
            current_cpu_percent: Number(avg(allProfiles.map((profile) => profile.cpuPercent)).toFixed(1)),
            avg_cpu_percent: Number(avg(allProfiles.map((profile) => profile.cpuPercent - 3.8)).toFixed(1)),
            max_cpu_percent: Number(max(allProfiles.map((profile) => profile.cpuMax)).toFixed(1)),
          },
          threads: {
            current_count: Math.round(avg(allProfiles.map((profile) => profile.threadCount))),
            current_active: Math.round(avg(allProfiles.map((profile) => profile.threadActive))),
            current_blocked: Math.round(avg(allProfiles.map((profile) => profile.threadBlocked))),
            current_waiting: Math.round(avg(allProfiles.map((profile) => profile.threadWaiting))),
            avg_count: Number(avg(allProfiles.map((profile) => profile.threadCount - 1.5)).toFixed(1)),
          },
          agents: [...DEMO_APM_AGENTS],
          metrics_count: 1440,
        };
      }

      return {
        memory: {
          current_heap_percent: agentProfile.heapPercent,
          current_heap_used: agentProfile.heapUsed,
          current_heap_total: agentProfile.heapTotal,
          current_heap_max: agentProfile.heapMax,
          current_rss: agentProfile.rss,
          avg_heap_percent: Number(Math.max(agentProfile.heapPercent - 4.2, 18).toFixed(1)),
          max_heap_percent: Number(Math.min(agentProfile.heapPercent + 9.1, 98).toFixed(1)),
        },
        cpu: {
          current_cpu_percent: agentProfile.cpuPercent,
          avg_cpu_percent: Number(Math.max(agentProfile.cpuPercent - 3.8, 2).toFixed(1)),
          max_cpu_percent: agentProfile.cpuMax,
        },
        threads: {
          current_count: agentProfile.threadCount,
          current_active: agentProfile.threadActive,
          current_blocked: agentProfile.threadBlocked,
          current_waiting: agentProfile.threadWaiting,
          avg_count: Number(Math.max(agentProfile.threadCount - 1.7, 1).toFixed(1)),
        },
        agents: [...DEMO_APM_AGENTS],
        metrics_count: 480,
      };
    }
    if (cleanEndpoint.includes('/metrics/timeseries')) {
      const metric = apmParams.get('metric') || 'heap_percent';
      const now = new Date('2025-01-15T15:00:00Z');
      const nowMs2 = now.getTime();
      const points: Array<{ timestamp: string; value: number }> = [];

      // Use a seeded pseudo-random to keep data stable across re-renders
      let seed = hashString(`${selectedAgent || 'all'}:${metric}`) || 17;
      const pseudoRandom = () => {
        seed = (seed * 16807 + 0) % 2147483647;
        return (seed - 1) / 2147483646;
      };

      for (let i = 23; i >= 0; i--) {
        const ts = new Date(nowMs2 - i * 3600000).toISOString();
        let value;
        if (metric === 'cpu_usage') {
          value = Math.max(0, agentProfile.cpuPercent - 8 + pseudoRandom() * 12 + (i < 6 ? 8 : 0));
        } else if (metric === 'heap_percent') {
          value = Math.max(0, agentProfile.heapPercent - 10 + pseudoRandom() * 12 + (i < 6 ? 6 : 0));
        } else if (metric === 'thread_count') {
          value = Math.max(1, agentProfile.threadCount - 3 + Math.floor(pseudoRandom() * 6));
        } else {
          value = 30 + pseudoRandom() * 20;
        }
        points.push({ timestamp: ts, value });
      }
      return { data: points };
    }
    if (cleanEndpoint.includes('/metrics')) {
      return {
        data: [
          {
            id: `apm-m-${selectedAgent || 'all'}-001`,
            agent_name: selectedAgent || 'customer-support-agent',
            timestamp: '2025-01-15T14:00:00Z',
            cpu_usage: Number(agentProfile.cpuPercent.toFixed(1)),
            heap_percent: Number(agentProfile.heapPercent.toFixed(1)),
            thread_count: agentProfile.threadCount,
            rss: agentProfile.rss,
          },
          {
            id: `apm-m-${selectedAgent || 'all'}-002`,
            agent_name: selectedAgent || 'customer-support-agent',
            timestamp: '2025-01-15T13:00:00Z',
            cpu_usage: Number(Math.max(agentProfile.cpuPercent - 6.4, 0).toFixed(1)),
            heap_percent: Number(Math.max(agentProfile.heapPercent - 5.8, 0).toFixed(1)),
            thread_count: Math.max(agentProfile.threadCount - 2, 1),
            rss: agentProfile.rss - 18874368,
          }
        ],
        meta: { page: 1, limit: 50, total: 2, totalPages: 1 }
      };
    }
    if (cleanEndpoint.includes('/gc-events')) {
      if (cleanEndpoint.includes('/stats')) {
        return {
          total_events: 847,
          avg_duration_ms: 45.3,
          max_duration_ms: 182.7,
          total_reclaimed_bytes: 2684354560,  // ~2.5 GB reclaimed
          by_type: {
            minor: 782,
            major: 58,
            full: 7,
          },
          avg_pause_ms: 38.6,
          max_pause_ms: 182.7,
        };
      }
      return {
        data: [
          { id: 'gc-001', timestamp: '2025-01-15T14:30:00Z', type: 'minor', pause_ms: 45, heap_before: 512, heap_after: 380 },
          { id: 'gc-002', timestamp: '2025-01-15T13:15:00Z', type: 'major', pause_ms: 180, heap_before: 720, heap_after: 290 },
          { id: 'gc-003', timestamp: '2025-01-15T12:00:00Z', type: 'minor', pause_ms: 38, heap_before: 480, heap_after: 350 }
        ],
        meta: { page: 1, limit: 50, total: 3, totalPages: 1 }
      };
    }
    if (cleanEndpoint.includes('/alert-rules')) {
      return [
        { id: 'rule-001', name: 'High Memory Usage', metric: 'heap_percent', operator: '>', threshold: 70, severity: 'warning', enabled: true },
        { id: 'rule-002', name: 'High CPU Usage', metric: 'cpu_usage', operator: '>', threshold: 80, severity: 'critical', enabled: true }
      ];
    }
    if (cleanEndpoint.includes('/alerts')) {
      const alerts = activeAgents
        .map((agent) => buildDemoAPMAlert(agent, getDemoAPMAgentProfile(agent)))
        .filter(Boolean);
      return {
        data: alerts,
        meta: { page: 1, limit: 50, total: alerts.length, totalPages: 1 }
      };
    }
    return {};
  }

  // === Safety Results (GenAIOps safety) ===
  if (cleanEndpoint.includes('/safety/results')) {
    return { data: [], meta: { page: 1, limit: 50, total: 0, totalPages: 1 } };
  }

  // Default fallback
  return {};
};

// ============================================================================
// EXPORT ALL MOCK DATA
// ============================================================================

// ============================================================================
// WORKLOADS
// ============================================================================

export const WORKLOAD_AI_PROD_ID = 'w0000000-0000-4000-a000-000000000001';
export const WORKLOAD_NON_PROD_ID = 'w0000000-0000-4000-a000-000000000002';
export const WORKLOAD_DATA_PIPELINE_ID = 'w0000000-0000-4000-a000-000000000003';

export const mockWorkloads = [
  {
    id: WORKLOAD_AI_PROD_ID,
    name: 'Marketing AI Platform',
    description: 'Marketing AI platform including content generation, personalization, and campaign optimization',
    client_id: DEMO_CLIENT_ID,
    tag_pairs: [
      { tag_key: 'Workload', tag_value: 'marketing-ai', operator: null },
    ],
    account_ids: [],
    account_names: [],
    project_ids: [PROJECT_AI_TRANSFORM_ID],
    project_names: ['AI Transformation Initiative'],
    created_at: '2025-10-15T10:00:00Z',
    updated_at: '2026-03-20T14:30:00Z',
  },
  {
    id: WORKLOAD_NON_PROD_ID,
    name: 'Financial Data Pipeline',
    description: 'Financial data processing pipelines for reporting, reconciliation, and analytics',
    client_id: DEMO_CLIENT_ID,
    tag_pairs: [
      { tag_key: 'Workload', tag_value: 'financial-pipeline', operator: null },
    ],
    account_ids: [],
    account_names: [],
    project_ids: [PROJECT_AI_TRANSFORM_ID, PROJECT_CLOUD_MIGRATION_ID],
    project_names: ['AI Transformation Initiative', 'Cloud Migration'],
    created_at: '2025-11-01T09:00:00Z',
    updated_at: '2026-03-18T11:00:00Z',
  },
  {
    id: WORKLOAD_DATA_PIPELINE_ID,
    name: 'Legacy Application',
    description: 'Legacy monolithic application being migrated to cloud-native architecture',
    client_id: DEMO_CLIENT_ID,
    tag_pairs: [
      { tag_key: 'Workload', tag_value: 'legacy-app', operator: null },
    ],
    account_ids: [],
    account_names: [],
    project_ids: [PROJECT_DATA_LAKE_ID],
    project_names: ['Data Lake Modernization'],
    created_at: '2025-12-10T15:00:00Z',
    updated_at: '2026-03-25T09:45:00Z',
  },
];

// Mock AWS accounts for demo
export const mockAWSAccounts = [
  {
    id: 'acc-0000-0000-0000-000000000001',
    account_id: '123456789012',
    account_type: 'standard',
    client_id: DEMO_CLIENT_ID,
    environment_names: ['Production', 'Staging'],
  },
  {
    id: 'acc-0000-0000-0000-000000000002',
    account_id: '987654321098',
    account_type: 'standard',
    client_id: DEMO_CLIENT_ID,
    environment_names: ['Development', 'QA'],
  },
  {
    id: 'acc-0000-0000-0000-000000000003',
    account_id: '111222333444',
    account_type: 'payer',
    client_id: DEMO_CLIENT_ID,
    environment_names: [],
  },
];

// Mock AWS environments for demo
export const mockAWSEnvironments = [
  {
    id: 'env-0000-0000-0000-000000000001',
    name: 'Production',
    environment_type: 'production',
    aws_account_id: 'acc-0000-0000-0000-000000000001',
    client_id: DEMO_CLIENT_ID,
    aws_account: { id: 'acc-0000-0000-0000-000000000001', account_id: '123456789012', account_type: 'standard' },
  },
  {
    id: 'env-0000-0000-0000-000000000002',
    name: 'Staging',
    environment_type: 'staging',
    aws_account_id: 'acc-0000-0000-0000-000000000001',
    client_id: DEMO_CLIENT_ID,
    aws_account: { id: 'acc-0000-0000-0000-000000000001', account_id: '123456789012', account_type: 'standard' },
  },
  {
    id: 'env-0000-0000-0000-000000000003',
    name: 'Development',
    environment_type: 'development',
    aws_account_id: 'acc-0000-0000-0000-000000000002',
    client_id: DEMO_CLIENT_ID,
    aws_account: { id: 'acc-0000-0000-0000-000000000002', account_id: '987654321098', account_type: 'standard' },
  },
  {
    id: 'env-0000-0000-0000-000000000004',
    name: 'QA',
    environment_type: 'qa',
    aws_account_id: 'acc-0000-0000-0000-000000000002',
    client_id: DEMO_CLIENT_ID,
    aws_account: { id: 'acc-0000-0000-0000-000000000002', account_id: '987654321098', account_type: 'standard' },
  },
];

// ============================================================================
// WORKLOAD ENRICHMENT - Auto-add workload_id/workload_name to all resources
// ============================================================================

const PROJECT_TO_WORKLOAD: Record<string, { id: string; name: string }> = {
  [PROJECT_AI_TRANSFORM_ID]: { id: WORKLOAD_AI_PROD_ID, name: 'Marketing AI Platform' },
  [PROJECT_CLOUD_MIGRATION_ID]: { id: WORKLOAD_NON_PROD_ID, name: 'Financial Data Pipeline' },
  [PROJECT_DATA_LAKE_ID]: { id: WORKLOAD_DATA_PIPELINE_ID, name: 'Legacy Application' },
};

// Map projects to AWS accounts and environments for demo filtering
const PROJECT_TO_ACCOUNT: Record<string, string> = {
  [PROJECT_AI_TRANSFORM_ID]: '123456789012',
  [PROJECT_CLOUD_MIGRATION_ID]: '987654321098',
  [PROJECT_DATA_LAKE_ID]: '123456789012',
};

const ALL_DEMO_ENVIRONMENTS = [
  'env-0000-0000-0000-000000000001', // Production
  'env-0000-0000-0000-000000000002', // Staging
  'env-0000-0000-0000-000000000003', // Development
  'env-0000-0000-0000-000000000004', // QA
];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function enrichWithWorkload<T extends Record<string, any>>(items: T[]): T[] {
  return items.map((item, index) => {
    const projectId = item.project_id as string | undefined;
    const wl = projectId ? PROJECT_TO_WORKLOAD[projectId] : undefined;
    const accountId = projectId ? PROJECT_TO_ACCOUNT[projectId] : undefined;
    // Distribute across environments: prod account → prod/staging, dev account → dev/qa
    const envIndex = accountId === '123456789012'
      ? (index % 2 === 0 ? 0 : 1)
      : (index % 2 === 0 ? 2 : 3);
    return {
      ...item,
      ...(wl ? { workload_id: wl.id, workload_name: wl.name } : {}),
      ...(accountId ? { account_id: accountId, cloud_account_id: accountId } : {}),
      ...(projectId ? { environment_id: ALL_DEMO_ENVIRONMENTS[envIndex] } : {}),
    };
  });
}

export const mockData = {
  projects: mockProjects,
  workloads: mockWorkloads,
  awsAccounts: mockAWSAccounts,
  awsEnvironments: mockAWSEnvironments,
  clients: mockClients,
  timesheets: enrichWithWorkload(mockTimesheets),
  milestones: enrichWithWorkload(mockMilestones),
  supportTickets: mockSupportTickets,
  costs: mockCosts,
  budgets: mockBudgets,
  tagging: mockTaggingCompliance,
  guardrails: mockGuardrails,
  datasets: enrichWithWorkload(mockDatasets),
  dataQualityRuns: enrichWithWorkload(mockDataQualityRuns),
  genaiTraces: enrichWithWorkload(mockGenAITraces),
  prompts: enrichWithWorkload(mockPrompts),
  evaluations: enrichWithWorkload(mockEvaluations),
  agentRuns: enrichWithWorkload(mockAgentRuns),
  agentTools: mockAgentTools,
  safetyViolations: enrichWithWorkload(mockSafetyViolations),
  incidents: enrichWithWorkload(mockIncidents),
  dashboardStats: mockDashboardStats,
  userAuth: mockUserAuth,
  users: mockUsers,
  employees: mockUsers
};

export default mockData;
